// cSpell:ignore Aandeel aandeel adres afgerekend afrekening Andere Berekening betalen dagen downloaden Duizendsten Eigenaar Eigenaarsdeel eigenaarsdeel forfait Gebouw gebouw gemeenschappelijk Gemeenschappelijke geven huurde Huurder huurder huurdersdeel Jaarlijks Kost kost kosten Kosten laste Leegstand leveranciers onderhoud oppervlakte overschot Overzicht pand panden pcts Periode Poetsdienst Privaat privaat Privatieve privé Prorata provisies Saldo Scenariolaan Smovin's Tarief terug Terug Totaal Totale Tuinonderhoud verbruik Verdeling vertrek volgende volgens Werkelijk
/* eslint-disable no-nested-ternary */
import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Banner,
  Button,
  Cell,
  DataTable,
  Stack,
  TextStyle,
  Tooltip,
} from "@rentiohq/web-shared/dist/components";
import { FontWeights } from "@rentiohq/web-theme";
import React from "react";
import { StringParam } from "serialize-query-params";
import styled from "styled-components";
import {
  buildingAddressOf,
  isUnitBreakdownKey,
  provisionSliceLines,
} from "../../../../buildingSettlement.utils";
import {
  Money,
  moneyFont,
  MoneyWithVat,
  MoneyText,
} from "../../../../components/Money";
import { OccupiedDays } from "../../../../components/OccupiedDays";
import { setCostDrawerSplit } from "../../../../costDrawerSplit";
import {
  departureLeaseEnd,
  settledLeaseEndChange,
  TDepartureLeaseEnd,
} from "../../../../departure.utils";
import {
  departureCorrectionLabel,
  ICorrectionsBySettlement,
  IDepartureCorrection,
  leaseEndCorrectedIn,
} from "../../../../departureCorrection.utils";
import {
  ComputeResult,
  departedDaysCents,
  departureDiffsByCat,
  isPerOccupant,
  OwnerCostLine,
  OwnerDepartureDiff,
  ownerHasPart,
} from "../../../../engine";
import {
  fixedFeeDaysOfLease,
  settledLeaseDaysWithin,
} from "../../../../fixedFeePeriods.utils";
import {
  keySplitsForCosts,
  unitAddressLabel,
} from "../../../../groupUnits.utils";
import { keyDisplayName } from "../../../../keyName.utils";
import { fromCents, roundCents, toCents } from "../../../../money.utils";
import { formatWeight } from "../../../../numberInput.utils";
import {
  IOccupiedDays,
  occupiedDaysText,
} from "../../../../occupiedDays.utils";
import {
  ownerBaseCauseOf,
  ownerBaseParts,
  ownerCorrectionOf,
  ownerLineCauses,
  TOwnerBaseCause,
} from "../../../../ownerLineCauses.utils";
import { ownerKeyShares, ownerPartKeysOf } from "../../../../ownerShare.utils";
import type { ProvisionSlice } from "../../../../provisionHistory";
import { provisionCalculation } from "../../../../provisionLineRows.utils";
import {
  describeProvisionPeriods,
  formatMonthCount,
  IInfoRow,
} from "../../../../provisionMonthsHint.utils";
import {
  calculationOf,
  daysFactor,
  ICalculationFactor,
  pctFactor,
  ratioFactor,
  sumFactor,
} from "../../../../rowCalculation.utils";
import {
  COMMON_KIND,
  ISettlementKind,
  ISettlementKindProvision,
  kindBalanceText,
  kindLabel,
  kindsAddUp,
  PRIVATE_KIND,
} from "../../../../settlementKinds.utils";
import {
  categorySpecOf,
  compareBreakdownKeys,
  IOwnerCardRow,
  ownerCardRows,
  ownerPartKeyName,
  withCategorySpecs,
} from "../../../../settlementView.utils";
import {
  ApportionmentKey,
  Category,
  CATEGORY_LABELS_NL,
  CONSUMPTION_UNIT,
  ContractRef,
  extraProvisionTitle,
  IAlreadySettledLine,
  Invoice,
  leaseProvisionOf,
  methodLabel,
  PerContractResult,
  resolveDistributionMethod,
  settlementEligible,
  vatLabel,
  VatValue,
} from "../../../../types";
import {
  commonVatRate,
  IVatRateLine,
  IVatRateRow,
  vatByRate,
} from "../../../../vatByRate.utils";
import {
  chosenCosts,
  getDraftSettlementName,
  getNoCostsText,
  isNothingToSettle,
} from "../settlementWizard.utils";
import { isFoldable, visibleCardRows } from "./cardRowLimit.utils";
import { ContractOverlapWarning } from "./ContractOverlapWarning";
import {
  COST_KIND_ORDER,
  CostKindBar,
  costKindLabel,
  costKindOf,
  TCostKind,
} from "./costKinds";
import {
  costShareCalculation,
  draftCostShareContext,
  ownerVacancyOf,
  ICostShareContext,
  ICostShareSplitFigures,
  ICostSplitParty,
  ownerReadingVacancyText,
  sentenceCaseOf,
} from "./costShareCalculation.utils";
import { IInfoTipRow, InfoTip } from "./InfoTip";
import { CELL_PADDING } from "./provisionColumns";
import {
  btwOf,
  CollapsibleCard,
  fmt,
  fmtDate,
  overlapDays,
  ownerUnitsNote,
  parentNote,
  PartyTitle,
  StepHeader,
} from "./shared";
import { DepartureSettledNote } from "./StepPeriod";
import {
  buildCatMeta,
  CatMeta,
  CategoryDistribution,
  ICardDistribution,
  PRIVATE_SUFFIX,
} from "./summaryCards.utils";
import {
  BodyCell,
  CardTotalLines,
  HEADER_CONTENT_TYPE,
  SummaryTable,
  TColumnAlign,
} from "./summaryTable.styles";
import { StepContext } from "./types";

// The totals lines of the "Al afgerekend bij vertrek" card.
const SummaryBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SummaryLine = styled.div<{ emphasized?: boolean }>`
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 24px;
  .label {
    color: ${({ theme }) => theme.getColor("gray", 800)};
    font-weight: ${({ emphasized }) =>
      emphasized ? FontWeights.Medium : FontWeights.Regular};
  }
  .value {
    min-width: 120px;
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-weight: ${({ emphasized }) =>
      emphasized ? FontWeights.Bold : FontWeights.Regular};
    ${moneyFont}
  }
`;

// The category of a breakdown key, without the internal "(privé)" marker.
const categoryOfKey = (key: string): Category =>
  (key.endsWith(PRIVATE_SUFFIX)
    ? key.slice(0, -PRIVATE_SUFFIX.length)
    : key) as Category;

// NL category name for a breakdown key. The internal "(privé)" marker is dropped
// from the display — the row's tag ("Gebouw", "Privaat") tells them apart.
const categoryLabel = (key: string): string => {
  // A departure's correction names its days (decision 3).
  const correction = departureCorrectionLabel(key);
  if (correction) return correction;
  const cat = categoryOfKey(key);
  return CATEGORY_LABELS_NL[cat] ?? cat;
};

// The category as a row shows it: its name with what the broker specified for
// an "Other" cost ("Andere — Tuinonderhoud").
const rowCategoryLabel = (key: string, meta?: CatMeta): string =>
  withCategorySpecs(categoryLabel(key), meta?.categorySpecs);

// ── The party cards ─────────────────────────────────────────────────────────
// One table per party with the calculation in it, after Smovin's "Detail
// afrekening" (Lewis, 2026-10-18): a light grey row per kind of cost, the
// common costs first, each with a "?" that says what that kind is; under
// it one row per invoice, by category and then by date, the category first
// and the invoice small under it; the totals under the table, right-aligned
// as a classic totals block:
//
//   Kost                  Periode          Totale kost  Aandeel ?  Prorata ?  Te betalen
//   Gemeenschappelijke kosten ?
//   Lift                  01/09/2026 →        € 700,00     10/85       30/30      € 82,36
//   Jaarlijks onderhoud   30/09/2026    + € 147,00 btw                       + € 17,30 btw
//   Privatieve kosten ?
//   Internet              01/07/2026 →        € 120,00       —         92/92     € 120,00
//                         30/09/2026     + € 25,20 btw                       + € 25,20 btw
//                                                 Totaal kosten excl. btw      € 202,36
//                                                                     Btw       € 42,50
//                                               Totaal kosten incl. btw ⓘ      € 244,86
//                                                        Totaal provisies ⓘ    − € 640,00
//                                                            Terug te geven      € 395,14
//
// That is the wizard's summary step (Lewis, 2026-10-19). The saved
// settlement's Detail tab drops the "Aandeel" and "Prorata" columns for
// the room the cost needs and explains each amount in an "i" before it,
// the way the totals put their "i" between label and amount: the unit's
// share by its key ("Aandeel: 10/85 (Duizendsten)"), the days the lease
// ran ("Prorata: 30/30 dagen (…)"), an owner's cause ("Vast eigenaarsdeel:
// 10%", "Leegstand: 92/92 dagen (…)") and the whole sum ("Berekening:
// € 700,00 × 10/85 × 30/30 = € 82,36").
// One card, one code path: its variant picks the columns (CARD_LAYOUTS).
//
// Every amount reads as on the rest of the platform (Lewis, 2026-10-19):
// excl. VAT, its VAT small under it, "+ € 17,30 btw"; the totals say
// excl. VAT, the VAT and incl. VAT, the provisions and the balance being
// incl. VAT.
//
// Each row's amount is the engine's own figure for that invoice (a
// tenant's costByInvoice and vatByInvoice, an owner's lines), never split
// again here, so the rows add up to "Totaal kosten" to the cent. A
// category whose invoices do not say (an older snapshot) keeps one row of
// its own figure. In the wizard a click on a row opens its cost; the
// confirmed card's rows open nothing (Lewis, 2026-10-19). Who carries the
// rest of the unit's share of an invoice sits in the "i" of the costs.

// Where a card is shown (Lewis, 2026-10-19): the wizard's summary step
// ("wizard"), or a saved settlement's Detail tab ("confirmed").
export type TCardVariant = "wizard" | "confirmed";

type TSummaryColumnKey =
  | "cost"
  | "period"
  | "value"
  | "share"
  | "occupied"
  | "info"
  | "amount";

interface ISummaryColumn {
  key: TSummaryColumnKey;
  // None for the "i" of the amounts; the amount column's comes with its
  // card (CardTable).
  heading?: string;
  // What the column means, in a "?" after its heading (Lewis, 2026-10-19).
  help?: string;
  // None for the confirmed card's cost: it takes what the others leave.
  width?: string;
  narrowWidth?: string;
  // Not shown in the narrow layout: its content moves into another cell
  // (the occupied days after the share, the period under the cost).
  isWideOnly?: boolean;
  align?: TColumnAlign;
}

// Each variant's columns, on the same widths in each card, so the cards
// line up under each other, and the table width under which it takes its
// narrow layout.
//
// The wizard's six columns hold their longest word ("€ 12.345,67",
// "01/07/2026 →", "+ € 2.592,67 btw", "120/900 kWh") from 600px on;
// narrower, the occupied days follow the unit's share. The period breaks
// after its arrow, so that column stays narrow; the occupied days are a
// ratio on one line ("245/365"). Kost is the widest (Lewis, 2026-10-19):
// its text no longer runs up against Periode, which sits about midway
// between a cost's text and its total. "Leegstand" needs all of its 13% in
// a 608px table.
//
// The confirmed card's columns hold theirs ("+ € 2.592,67 btw": 116px)
// from 570px on, so the settlement drawer's 608px table on a 1440px screen
// keeps them, a scrollbar of its own included; narrower, a row's period
// goes small under its cost. The "i" of an amount sits in a slot of its own
// just before it, as a total's "i" before its amount, so the amounts stay
// in one line with the totals'.
const CARD_LAYOUTS: Record<
  TCardVariant,
  { columns: ISummaryColumn[]; narrowBelow: number }
> = {
  wizard: {
    narrowBelow: 600,
    columns: [
      {
        key: "cost",
        heading: "settlement.summary.card.column_cost",
        width: "23%",
        narrowWidth: "22%",
      },
      {
        key: "period",
        heading: "costs.field.period",
        width: "16%",
        narrowWidth: "18%",
      },
      {
        key: "value",
        heading: "settlement.summary.card.column_total_cost",
        width: "17%",
        narrowWidth: "19%",
        align: "right",
      },
      {
        key: "share",
        heading: "settlement.summary.card.column_unit_share",
        help: "settlement.summary.card.column_unit_share.help",
        width: "15%",
        narrowWidth: "22%",
        align: "center",
      },
      {
        key: "occupied",
        heading: "settlement.summary.card.column_occupied",
        help: "settlement.summary.card.column_occupied.help",
        width: "13%",
        isWideOnly: true,
        align: "center",
      },
      {
        key: "amount",
        width: "16%",
        narrowWidth: "19%",
        align: "right",
      },
    ],
  },
  confirmed: {
    narrowBelow: 570,
    columns: [
      { key: "cost", heading: "settlement.summary.card.column_cost" },
      {
        key: "period",
        heading: "costs.field.period",
        width: "17%",
        isWideOnly: true,
      },
      {
        key: "value",
        heading: "settlement.summary.card.column_total_cost",
        width: "23%",
        narrowWidth: "29%",
        align: "right",
      },
      { key: "info", width: "24px", narrowWidth: "24px", align: "right" },
      { key: "amount", width: "23%", narrowWidth: "29%", align: "right" },
    ],
  },
};

// The width of an element, followed as it changes: the same card sits in
// the wizard and in the settlement drawer.
const useWidthOf = (): [React.RefObject<HTMLDivElement>, number] => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [width, setWidth] = React.useState(0);
  React.useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(entries => {
      setWidth(entries[0]?.contentRect.width ?? 0);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
};

// The indent of an owner's cause rows under the row of their cost.
const CAUSE_INDENT = 16;

const InvoiceRow = styled.tr<{
  isClickable?: boolean;
  $layout?: ICardRow["layout"];
}>`
  ${({ $layout }) =>
    $layout === "costHead"
      ? `
    td {
      border-bottom: 0;
      padding-bottom: 2px;
    }`
      : ""}
  ${({ $layout }) =>
    $layout === "cause"
      ? `
    td:first-child {
      padding-left: calc(${CELL_PADDING} + ${CAUSE_INDENT}px);
    }`
      : ""}
  ${({ isClickable, theme }) =>
    isClickable
      ? `
    cursor: pointer;
    &:hover td,
    &:focus-visible td {
      background: ${theme.colors.neutral10};
    }
    &:focus-visible {
      outline: 2px solid ${theme.colors.green60};
      outline-offset: -2px;
    }`
      : ""}
`;

const StatementAction = styled.div`
  margin-top: 16px;
`;

// One row of a card: an invoice, or on an older snapshot a category's
// share as one figure. An owner's invoice with both a fixed share and a
// vacancy is a row of the cost ("costHead": its period and total, no
// share) with a row per cause indented under it ("cause": its share, its
// empty days and its amount; Lewis, 2026-10-19).
interface ICardRow {
  id: string;
  layout?: "costHead" | "cause";
  // The first line: the category ("Lift", "Andere — Tuinonderhoud"), or a
  // cause ("Vast eigenaarsdeel (10%)").
  title: React.ReactNode;
  // Small and muted under it: the invoice's description, "Jaarlijks
  // onderhoud", when it says more than the category (Lewis, 2026-10-19).
  sub?: string;
  // An owner's invoice with one cause: that cause, small under the rest.
  cause?: React.ReactNode;
  // What the "i" of the costs names the row by: the invoice's description.
  name: string;
  period?: { from: string; to: string };
  // The invoice's total incl. VAT, and the VAT in it.
  value?: number;
  valueVat?: number;
  // The wizard's "Aandeel": "10/85", "60%", "Eigenaarsdeel …"; a dash
  // when it says nothing.
  split: React.ReactNode;
  // The wizard's "Prorata": the days the lease ran of the cost's period
  // ("31/92"), or an owner's unit stood empty.
  occupied?: IOccupiedDays;
  // The confirmed card's "i" before the amount: how it came about, the
  // unit's share, the days, an owner's cause and the whole sum. A tenant's
  // row builds it only when the row shows (N10).
  info?: IInfoTipRow[] | (() => IInfoTipRow[]);
  // The party's share incl. VAT, and the VAT in it.
  amount: number;
  amountVat?: number;
  // The VAT rate of the row's costs, for the VAT per rate; undefined when
  // they carry several (an older snapshot's category row).
  vatRate?: VatValue;
  // The cost a click on the row opens, in the wizard.
  invoiceId?: string;
  // Who carries the rest of the unit's share of the invoice, for the "i"
  // of the costs.
  rest?: string;
}

// The rows of one invoice (an owner's difference with a departure
// settlement stays under its invoice), or of a category on an older
// snapshot, with what they are sorted on.
interface ICardBlock {
  key: string;
  date?: string;
  rows: ICardRow[];
}

// A kind of cost: its grey row, and its rows under it.
interface ICardSection {
  kind: TCostKind;
  rows: ICardRow[];
}

// The common costs, then the private ones, a kind only when it has rows;
// within a kind by category, then by date.
const cardSections = (blocks: ICardBlock[]): ICardSection[] =>
  COST_KIND_ORDER.map(kind => ({
    kind,
    rows: blocks
      .filter(block => costKindOf(block.key) === kind)
      .sort(
        (a, b) =>
          compareBreakdownKeys(a.key, b.key) ||
          (a.date ?? "").localeCompare(b.date ?? ""),
      )
      .flatMap(block => block.rows),
  })).filter(section => section.rows.length > 0);

// A total under the table: its label with an "i", and its amount.
export interface ICardTotal {
  id: string;
  label: string;
  info?: {
    rows: IInfoRow[];
    note?: string;
    // Content of its own (the VAT per rate), named by `label`.
    content?: React.ReactNode;
    label?: string;
  };
  amount?: number;
  // The amount as text, for one with a sign ("− € 960,00").
  amountText?: string;
  isFinal?: boolean;
}

// "01/07/2026 → 30/09/2026", both days with their year (Lewis,
// 2026-10-19), breaking after the arrow when the column is narrow, never
// inside a date.
const PeriodText: React.FC<{ period?: { from: string; to: string } }> = ({
  period,
}) => {
  if (!period) return <TextStyle variation="subdued">—</TextStyle>;
  return (
    <TextStyle variation="subdued" size="small">
      <span className="nowrap">{fmtDate(period.from)} →</span>{" "}
      <span className="nowrap">{fmtDate(period.to)}</span>
    </TextStyle>
  );
};

const Dash: React.FC = () => <TextStyle variation="subdued">—</TextStyle>;

// An amount incl. VAT as the platform shows it: excl. VAT, its VAT small
// under it; on its own when the VAT is unknown (an older snapshot).
const AmountWithVat: React.FC<{ amount: number; vat?: number }> = ({
  amount,
  vat,
}) =>
  vat != null ? (
    <MoneyWithVat
      amount={fromCents(toCents(amount) - toCents(vat))}
      vat={vat}
    />
  ) : (
    <Money amount={amount} />
  );

// "31/92", the full sentence in its hover (OccupiedDays). In the unit
// share's style: the text's own color, small.
const OccupiedText: React.FC<{
  occupied: IOccupiedDays;
}> = ({ occupied }) => (
  <TextStyle size="small">
    <OccupiedDays occupied={occupied} />
  </TextStyle>
);

// The owner card's days column in the wizard: the days the unit stood
// empty, with what they mean in its "?" (Lewis, 2026-10-19), where a
// tenant's card has the days the lease ran.
const VACANCY_COLUMN = {
  heading: "settlement.summary.card.column_vacancy",
  help: "settlement.summary.card.column_vacancy.help",
};

// The slot of an amount's "i": no padding of its own, the icon against
// the amount's column.
const InfoCell = styled(BodyCell)`
  padding-left: 0;
  padding-right: 0;
`;

const CardTable: React.FC<{
  variant: TCardVariant;
  sections: ICardSection[];
  // The amount column's heading: "Te betalen", "Ten laste" (no "?": Lewis,
  // 2026-10-19).
  amountHeading: string;
  // An owner's card: its days column explains the empty days.
  isOwner?: boolean;
  // Makes a row with a cost open it (the wizard); without it the rows are
  // plain (the confirmed card, Lewis 2026-10-19).
  onOpenCost?: (invoiceId: string) => void;
}> = ({ variant, sections, amountHeading, isOwner, onOpenCost }) => {
  const [frameRef, width] = useWidthOf();
  // A long card lists its first costs per kind and folds the rest away
  // until the broker asks for them (visibleCardRows, N10).
  const [isExpanded, setIsExpanded] = React.useState(false);
  const layout = CARD_LAYOUTS[variant];
  const isNarrow = width > 0 && width < layout.narrowBelow;
  const columns = layout.columns.filter(
    column => !isNarrow || !column.isWideOnly,
  );
  const isShown = (key: TSummaryColumnKey) =>
    columns.some(column => column.key === key);
  const textsOf = (column: ISummaryColumn) =>
    isOwner && column.key === "occupied" ? VACANCY_COLUMN : column;
  const headingOf = (column: ISummaryColumn): React.ReactNode => {
    if (column.key === "amount") return amountHeading;
    const texts = textsOf(column);
    if (!texts.heading) return "";
    const heading = getLocalizedText(texts.heading);
    const help = texts.help ? getLocalizedText(texts.help) : undefined;
    // A heading and its "?" stay on one line (Lewis, 2026-10-19).
    return (
      <span className="nowrap">
        {heading}
        {help && <InfoTip help rows={[{ label: heading, value: help }]} />}
      </span>
    );
  };
  const occupiedOf = (occupied: IOccupiedDays) => (
    <OccupiedText occupied={occupied} />
  );
  const cellOf = (row: ICardRow, column: ISummaryColumn): React.ReactNode => {
    // A cost's row above its causes holds no share of it; its causes do
    // not repeat its period and total.
    if (
      row.layout === "costHead" &&
      ["share", "occupied", "info", "amount"].includes(column.key)
    ) {
      return null;
    }
    if (row.layout === "cause" && ["period", "value"].includes(column.key)) {
      return null;
    }
    switch (column.key) {
      case "cost":
        return (
          <>
            <div>{row.title}</div>
            {row.sub && (
              <TextStyle variation="subdued" size="small" element="div">
                {row.sub}
              </TextStyle>
            )}
            {row.cause && (
              <TextStyle variation="subdued" size="small" element="div">
                {row.cause}
              </TextStyle>
            )}
            {!isShown("period") && row.layout !== "cause" && row.period && (
              <div>
                <PeriodText period={row.period} />
              </div>
            )}
          </>
        );
      case "period":
        return <PeriodText period={row.period} />;
      case "value":
        if (row.value == null) return <Dash />;
        return <AmountWithVat amount={row.value} vat={row.valueVat} />;
      case "share":
        return (
          <TextStyle size="small">
            {row.split}
            {!isShown("occupied") && row.occupied && (
              <>
                {" · "}
                {occupiedOf(row.occupied)}
              </>
            )}
          </TextStyle>
        );
      case "occupied":
        return row.occupied ? occupiedOf(row.occupied) : <Dash />;
      case "info": {
        const info = typeof row.info === "function" ? row.info() : row.info;
        return info && info.length > 0 ? <InfoTip rows={info} /> : null;
      }
      default:
        return <AmountWithVat amount={row.amount} vat={row.amountVat} />;
    }
  };
  return (
    <div ref={frameRef}>
      <SummaryTable>
        <colgroup>
          {columns.map(column => (
            <col
              key={column.key}
              style={{
                width: isNarrow ? column.narrowWidth : column.width,
              }}
            />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map(column => (
              <Cell
                header
                key={column.key}
                content={headingOf(column)}
                contentType={HEADER_CONTENT_TYPE[column.align ?? "left"]}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {sections.length === 0 && (
            <tr>
              <BodyCell colSpan={columns.length}>
                <TextStyle variation="subdued">
                  {getLocalizedText("settlement.summary.no_assigned_costs")}
                </TextStyle>
              </BodyCell>
            </tr>
          )}
          {sections.map(section => {
            const visible = visibleCardRows({
              rows: section.rows,
              isExpanded,
            });
            return (
              <React.Fragment key={section.kind}>
                <CostKindBar kind={section.kind} colSpan={columns.length} />
                {visible.rows.map(row => {
                  const { invoiceId } = row;
                  const open =
                    onOpenCost && invoiceId
                      ? () => onOpenCost(invoiceId)
                      : undefined;
                  return (
                    <InvoiceRow
                      key={row.id}
                      $layout={row.layout}
                      isClickable={!!open}
                      onClick={open}
                      tabIndex={open ? 0 : undefined}
                      onKeyDown={event => {
                        if (open && event.key === "Enter") open();
                      }}
                    >
                      {columns.map(column => {
                        const Body =
                          column.key === "info" ? InfoCell : BodyCell;
                        return (
                          <Body key={column.key} align={column.align}>
                            {cellOf(row, column)}
                          </Body>
                        );
                      })}
                    </InvoiceRow>
                  );
                })}
                {visible.hiddenCount > 0 && (
                  <tr>
                    <BodyCell colSpan={columns.length}>
                      <Button
                        appearance="link"
                        onClick={() => setIsExpanded(true)}
                      >
                        {getLocalizedText("settlement.summary.card.show_all", {
                          count: String(
                            visible.rows.filter(row => row.layout !== "cause")
                              .length + visible.hiddenCount,
                          ),
                        })}
                      </Button>
                    </BodyCell>
                  </tr>
                )}
                {isFoldable({ rows: section.rows, isExpanded }) && (
                  <tr>
                    <BodyCell colSpan={columns.length}>
                      <Button
                        appearance="link"
                        onClick={() => {
                          setIsExpanded(false);
                          // Back to the card's head, not somewhere in the
                          // costs that fold away under the pointer.
                          frameRef.current?.scrollIntoView?.({
                            block: "nearest",
                          });
                        }}
                      >
                        {getLocalizedText("settlement.summary.card.show_less")}
                      </Button>
                    </BodyCell>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </SummaryTable>
    </div>
  );
};

export const CardTotals: React.FC<{ totals: ICardTotal[] }> = ({ totals }) => (
  <CardTotalLines>
    {totals.map(total => (
      <React.Fragment key={total.id}>
        <div className="total-label">
          <TextStyle variation={total.isFinal ? "strong" : undefined}>
            {total.label}
          </TextStyle>
        </div>
        <div className="total-info">
          {total.info && (
            <InfoTip
              rows={total.info.rows}
              note={total.info.note}
              content={total.info.content}
              label={total.info.label}
            />
          )}
        </div>
        <div className="total-amount">
          {total.amountText != null ? (
            <MoneyText variation={total.isFinal ? "strong" : undefined}>
              {total.amountText}
            </MoneyText>
          ) : total.amount != null ? (
            <Money
              amount={total.amount}
              variation={total.isFinal ? "strong" : undefined}
            />
          ) : null}
        </div>
      </React.Fragment>
    ))}
  </CardTotalLines>
);

// The span of a category's costs, within the tenant's lease when given.
const costsPeriod = (
  costs: Pick<Invoice, "periodFrom" | "periodTo">[],
  lease?: { from: string; to: string },
): { from: string; to: string } | undefined => {
  if (costs.length === 0) return undefined;
  const from = costs.map(c => c.periodFrom.slice(0, 10)).sort()[0];
  const to = costs
    .map(c => c.periodTo.slice(0, 10))
    .sort()
    .at(-1) as string;
  const leaseFrom = lease?.from?.slice(0, 10);
  const leaseTo = lease?.to?.slice(0, 10);
  const shownFrom = leaseFrom && leaseFrom > from ? leaseFrom : from;
  const shownTo = leaseTo && leaseTo < to ? leaseTo : to;
  return shownFrom <= shownTo ? { from: shownFrom, to: shownTo } : { from, to };
};

// "De Lift BV", "De Lift BV, Poetsdienst", or "3 leveranciers".
const MAX_NAMED_SUPPLIERS = 2;
const suppliersText = (costs: Invoice[]): string | undefined => {
  const names = [
    ...new Set(
      costs
        .map(cost => cost.supplier?.trim())
        .filter(Boolean)
        .map(name => sentenceCaseOf(name as string)),
    ),
  ];
  if (names.length === 0) return undefined;
  return names.length > MAX_NAMED_SUPPLIERS
    ? getLocalizedText("settlement.summary.card.suppliers_count", {
        count: String(names.length),
      })
    : names.join(", ");
};

// An invoice's name, in the "i" of the costs: its description.
const invoiceDescription = (inv: Invoice, fallback: string): string =>
  inv.description?.trim() || fallback;

// An invoice's row, first its category with what the broker specified for
// that cost ("Andere — Tuinonderhoud")...
const invoiceCategory = (key: string, inv: Invoice): string => {
  const spec = categorySpecOf(inv);
  return withCategorySpecs(categoryLabel(key), spec ? [spec] : undefined);
};

const isSameText = (a: string, b: string): boolean =>
  a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();

// ...then under it its description, unless it only repeats the category;
// no supplier or date there (Lewis, 2026-10-19).
const invoiceSub = (key: string, inv: Invoice): string | undefined => {
  const description = inv.description?.trim();
  if (!description) return undefined;
  const repeatsCategory = [
    categoryLabel(key),
    invoiceCategory(key, inv),
    categorySpecOf(inv),
  ].some(text => text != null && isSameText(text, description));
  return repeatsCategory ? undefined : description;
};

// What an invoice's rows are sorted on within their category.
const invoiceDate = (inv?: Invoice): string | undefined =>
  inv ? inv.date || inv.periodFrom : undefined;

// The invoice's total incl. VAT.
const invoiceValue = (inv: Invoice): number =>
  roundCents(inv.amountExclVat + btwOf(inv));

// "245/365": the days of the costs' period the lease ran, of that period.
// None for a reading or a company amount per tenant: no occupied-days share
// went into it. A lease whose kind was a forfait for part of the period
// counts only its other days, as the engine and the statement do (N71):
// "184/365".
const occupiedDaysOf = (params: {
  costs: Pick<Invoice, "periodFrom" | "periodTo">[];
  contract?: { from: string; to: string };
  perOccupant?: boolean;
  // The days the costs' kind was a forfait for this lease.
  fixedFeeDays?: { from: string; to: string }[];
}): IOccupiedDays | undefined => {
  const { costs, contract, perOccupant, fixedFeeDays } = params;
  if (!contract || perOccupant || costs.length === 0) return undefined;
  const span = costsPeriod(costs);
  if (!span) return undefined;
  return {
    days: settledLeaseDaysWithin({
      lease: contract,
      range: span,
      fixedFeeDays,
    }),
    total: overlapDays(span.from, span.to, span.from, span.to),
    period: span,
  };
};

// The "Aandeel" of a row's "i", as the cost's split names it.
const shareLabel = (): string => getLocalizedText("costs.detail.share");

// A key's share of a party's units (Lewis, 2026-10-18): their weight of the
// split's total, "300/1.000" or "1/3", and each unit of the split with its
// percentage. The costs of one category can split over different units (a
// unit archived before one of them began takes no part in it): one share
// per split these units took part in, each with its dates. An owner of
// several units reads their units' weight together ("2/3").
interface IKeyShare {
  weight: number;
  total: number;
  dates?: { from: string; to: string };
  units: { label: string; pct: number }[];
}

const keySharesOf = (params: {
  keyDef: ApportionmentKey;
  unitIds: string[];
  distribution: ICardDistribution;
  costPeriods: { from: string; to: string }[];
}): IKeyShare[] => {
  const { keyDef, unitIds, distribution, costPeriods } = params;
  const ownIds = unitIds.map(String);
  const splits = keySplitsForCosts({
    keyDef,
    units: distribution.units,
    periods: costPeriods,
    allUnits: distribution.allUnits,
    unitCount: distribution.unitCount,
  }).filter(split => split.total > 0);
  const isOwnUnitIn = (split: (typeof splits)[number]) => (unitId: string) =>
    split.units.some(unit => String(unit.id) === unitId);
  const ownSplits = splits.filter(split => ownIds.some(isOwnUnitIn(split)));
  const shown = splits.length > 1 && ownSplits.length > 0 ? ownSplits : splits;
  if (ownIds.length === 0) return [];
  // One unit's weight as the split gives it; several units' weights summed
  // over those that take part in the split.
  const weightOf = (split: (typeof shown)[number]) =>
    ownIds.length === 1
      ? split.weightOf(ownIds[0])
      : ownIds
          .filter(isOwnUnitIn(split))
          .reduce((sum, unitId) => sum + split.weightOf(unitId), 0);
  return shown.map(split => ({
    weight: weightOf(split),
    total: split.total,
    dates:
      splits.length > 1 && split.from && split.to
        ? { from: split.from, to: split.to }
        : undefined,
    units: split.units.map(unit => ({
      label: unit.address ?? unit.code,
      pct: Math.round((split.weightOf(unit.id) / split.total) * 100),
    })),
  }));
};

// "300/1.000".
const shareRatio = (share: IKeyShare): string =>
  `${formatWeight(share.weight)}/${formatWeight(share.total)}`;

// The key's factor in a row's sum: its one share; none when the costs'
// periods split over different units.
const keyShareFactor = (shares: IKeyShare[]): ICalculationFactor | undefined =>
  shares.length === 1
    ? ratioFactor(shares[0].weight, shares[0].total)
    : undefined;

// The wizard's "Aandeel" cell: only the ratio (Lewis, 2026-10-18), its
// hover the key's name, then each unit's share, as the summary did before.
const KeySplit: React.FC<{
  keyDef: ApportionmentKey;
  unitIds: string[];
  distribution: ICardDistribution;
  costPeriods: { from: string; to: string }[];
  // The hover's first line, the key's name by default.
  title?: string;
}> = ({ keyDef, unitIds, distribution, costPeriods, title }) => {
  const heading = title ?? keyDisplayName(keyDef.name);
  const shares = keySharesOf({ keyDef, unitIds, distribution, costPeriods });
  if (shares.length === 0) return <span>{heading}</span>;
  const tooltip = (
    <div style={{ minWidth: 200 }}>
      <div style={{ fontWeight: FontWeights.SemiBold, marginBottom: 6 }}>
        {heading}
      </div>
      {shares.map(share => (
        <div key={`${share.dates?.from ?? ""}-${share.dates?.to ?? ""}`}>
          {share.dates && (
            <div style={{ marginTop: 6 }}>
              {fmtDate(share.dates.from)} → {fmtDate(share.dates.to)}
            </div>
          )}
          {share.units.map(unit => (
            <div
              key={unit.label}
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 16,
              }}
            >
              <span>{unit.label}</span>
              <span>{unit.pct}%</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
  return (
    <Tooltip tooltipContent={tooltip}>
      <span style={{ cursor: "help" }}>
        {shares.length === 1
          ? shareRatio(shares[0])
          : shares.map(share => (
              <span
                key={`${share.dates?.from ?? ""}-${share.dates?.to ?? ""}`}
                style={{ display: "block" }}
              >
                {shareRatio(share)}
              </span>
            ))}
      </span>
    </Tooltip>
  );
};

// The confirmed card's "Aandeel" lines in the "i" of a row's amount (Lewis,
// 2026-10-19): the ratio with the key's name, "300/1.000 (Verdeling
// volgens oppervlakte)", a line per split with its dates; the key's name
// alone without one. No units under it: the wizard's hover lists them.
const keyShareRows = (name: string, shares: IKeyShare[]): IInfoTipRow[] =>
  shares.length === 0
    ? [{ label: shareLabel(), value: name }]
    : shares.map(share => ({
        label: shareLabel(),
        value: `${shareRatio(share)} (${name})${
          share.dates
            ? ` · ${fmtDate(share.dates.from)} → ${fmtDate(share.dates.to)}`
            : ""
        }`,
      }));

// "Prorata: 245/365 dagen (01/01/2026 – 31/12/2026)", the days the lease
// ran of the cost's period; none when the method has no days.
const proRataRows = (occupied?: IOccupiedDays): IInfoTipRow[] =>
  occupied
    ? [
        {
          label: getLocalizedText("costs.detail.split.days"),
          value: occupiedDaysText(occupied),
        },
      ]
    : [];

// "Berekening: € 1.000,00 × 10/85 × 245/365 = € 78,97", the last line of
// a row's "i" (see calculationOf); none when the sum cannot be shown.
const calculationRows = (params: {
  base: number;
  factors: (ICalculationFactor | undefined)[];
  amount: number;
}): IInfoTipRow[] => {
  const calculation = calculationOf(params);
  return calculation
    ? [
        {
          label: getLocalizedText("settlement.summary.card.calculation"),
          value: calculation.text,
        },
      ]
    : [];
};

// The tenant's or their unit's reading of the building's total.
const readingShareOf = (params: {
  key: string;
  pc: PerContractResult;
  split?: ICostShareSplitFigures;
  perOccupant?: boolean;
}):
  | { reading: number; total: number; text: string; hasExcess?: boolean }
  | undefined => {
  const { key, pc, split, perOccupant } = params;
  const category = categoryOfKey(key);
  const readings = split?.consumption?.[category] ?? {};
  const reading = perOccupant
    ? readings[`contract:${pc.contractId}`]
    : readings[pc.unitId];
  const total = split?.buildingTotal?.[category];
  if (reading == null || !(total != null && total > 0)) return undefined;
  const unit = CONSUMPTION_UNIT[category];
  // A share of the excess split by the key comes on top of the reading
  // (Y14): "30 m³ + aandeel overschot € 75,34", so the amount can be
  // checked; the reading's ratio alone no longer explains it.
  const excess = pc.excessByCat?.[key] ?? 0;
  if (Math.abs(excess) >= 0.005) {
    return {
      reading,
      total,
      hasExcess: true,
      text: getLocalizedText("settlement.summary.card.reading_plus_excess", {
        reading: `${formatWeight(reading)}${unit ? ` ${unit}` : ""}`,
        amount: fmt(excess),
      }),
    };
  }
  return {
    reading,
    total,
    // "120/900 kWh".
    text: `${formatWeight(reading)}/${formatWeight(total)}${
      unit ? ` ${unit}` : ""
    }`,
  };
};

// The tenant's part of a cost they share with the owner, as the engine
// takes it (tenantSharePctOf); all of any other cost of theirs.
const tenantPctOf = (inv: Invoice): number =>
  inv.target === "shared" ? inv.tenantSharePct ?? 0 : 100;

// The tenant's part of a unit's own cost when they carry only part of it
// ("60%"), the same for each of the costs; none otherwise.
const unitCostPctOf = (costs: Invoice[]): number | undefined => {
  const pcts = [
    ...new Set(
      costs.map(cost =>
        cost.target === "shared" ? cost.tenantSharePct ?? 100 : 100,
      ),
    ),
  ];
  return pcts.length === 1 && pcts[0] < 100 ? pcts[0] : undefined;
};

// How a tenant's share of an invoice came about, in the wizard's "Aandeel"
// cell: the key's share of their unit (the key and the units in its
// hover), their reading of the building's total (the method in its hover),
// the method of a metering company's amount, or their part of the unit's
// own cost ("60%"); a dash when it says nothing, as for a unit carrying
// all of its own cost (Lewis, 2026-10-19).
const tenantSplit = (params: {
  key: string;
  costs: Invoice[];
  pc: PerContractResult;
  distribution?: ICardDistribution;
  split?: ICostShareSplitFigures;
}): React.ReactNode => {
  const { key, costs, pc, distribution, split } = params;
  if (isUnitBreakdownKey(key)) {
    const pct = unitCostPctOf(costs);
    return pct != null ? (
      getLocalizedText("settlement.summary.card.split_pct", {
        pct: String(pct),
      })
    ) : (
      <Dash />
    );
  }
  const dist = distribution?.byCat[key];
  if (dist?.key && distribution) {
    return (
      <KeySplit
        keyDef={dist.key}
        unitIds={[pc.unitId]}
        distribution={distribution}
        costPeriods={costs.map(cost => ({
          from: cost.periodFrom,
          to: cost.periodTo,
        }))}
      />
    );
  }
  const reading =
    dist?.method === "consumption"
      ? readingShareOf({ key, pc, split, perOccupant: dist.perOccupant })
      : undefined;
  if (reading) {
    return (
      <Tooltip tooltipContent={dist?.label}>
        <span style={{ cursor: "help" }}>{reading.text}</span>
      </Tooltip>
    );
  }
  return dist?.label ?? <Dash />;
};

// The same in the confirmed card's "i" before the amount: the "Aandeel"
// lines, then the days the lease ran ("Prorata"), then for one invoice the
// whole sum from its cost to the tenant's amount, both excl. VAT
// ("Berekening"); none for a metering company's amount, which is no
// product of the cost.
const tenantAmountInfo = (params: {
  key: string;
  costs: Invoice[];
  pc: PerContractResult;
  distribution?: ICardDistribution;
  split?: ICostShareSplitFigures;
  occupied?: IOccupiedDays;
  // The tenant's amount excl. VAT, as the row shows it.
  amountExcl?: number;
}): IInfoTipRow[] => {
  const { key, costs, pc, distribution, split, occupied, amountExcl } = params;
  const dist = distribution?.byCat[key];
  const share = ((): {
    rows: IInfoTipRow[];
    factor?: ICalculationFactor;
    // The amount is no product of the cost (a reading with an excess share).
    noSum?: boolean;
  } => {
    if (isUnitBreakdownKey(key)) {
      const pct = unitCostPctOf(costs);
      return {
        rows:
          pct != null
            ? [
                {
                  label: shareLabel(),
                  value: getLocalizedText("settlement.summary.card.split_pct", {
                    pct: String(pct),
                  }),
                },
              ]
            : [],
      };
    }
    if (dist?.key && distribution) {
      const shares = keySharesOf({
        keyDef: dist.key,
        unitIds: [pc.unitId],
        distribution,
        costPeriods: costs.map(cost => ({
          from: cost.periodFrom,
          to: cost.periodTo,
        })),
      });
      return {
        rows: keyShareRows(keyDisplayName(dist.key.name), shares),
        factor: keyShareFactor(shares),
      };
    }
    const reading =
      dist?.method === "consumption"
        ? readingShareOf({ key, pc, split, perOccupant: dist.perOccupant })
        : undefined;
    if (reading) {
      return {
        rows: [{ label: shareLabel(), value: reading.text }],
        // With an excess share on top, the reading's ratio is not the sum.
        factor: reading.hasExcess
          ? undefined
          : ratioFactor(reading.reading, reading.total),
        noSum: reading.hasExcess,
      };
    }
    return {
      rows: dist?.label ? [{ label: shareLabel(), value: dist.label }] : [],
    };
  })();
  const [only] = costs;
  const hasSum =
    amountExcl != null &&
    costs.length === 1 &&
    dist?.method !== "company" &&
    !share.noSum;
  return [
    ...share.rows,
    ...proRataRows(occupied),
    ...(hasSum
      ? calculationRows({
          base: only.amountExclVat,
          factors: [
            share.factor,
            pctFactor(tenantPctOf(only)),
            daysFactor(occupied),
          ],
          amount: amountExcl,
        })
      : []),
  ];
};

// "€ 252,76 (3 kosten)".
export const costCountText = (amount: number, count: number): string => {
  if (count === 0) return fmt(amount);
  return count === 1
    ? getLocalizedText("settlement.summary.card.costs_count_one", {
        amount: fmt(amount),
      })
    : getLocalizedText("settlement.summary.card.costs_count_many", {
        amount: fmt(amount),
        count: String(count),
      });
};

// A provision comes off the tenant's total ("− € 600,00"); a negative one
// adds to it ("+ € 50,00").
const signedProvision = (amount: number): string => {
  if (amount > 0.005) return `− ${fmt(amount)}`;
  if (amount < -0.005) return `+ ${fmt(-amount)}`;
  return fmt(0);
};

// "Gemeenschappelijke kosten", "Privatieve kosten": the terms of the table's
// grey rows.
const sliceName = (slice: ProvisionSlice): string => costKindLabel(slice);

// "12 × € 60,00 = € 720,00", or "12 × gem. € 59,17 = € 710,00" when the
// months held other amounts (see provisionCalculation). Without the
// lease's monthly amount, a provision that divides into whole cents over
// whole months reads as that amount a month.
const provisionValue = (params: {
  amount: number;
  months?: number;
  monthly?: number;
}): string => {
  const { amount, months } = params;
  if (months == null || !(months > 0)) return fmt(amount);
  const cents = toCents(amount);
  const monthly =
    params.monthly ??
    (Number.isInteger(months) && cents % months === 0
      ? fromCents(cents / months)
      : undefined);
  const calc = provisionCalculation({ computed: amount, months, monthly });
  if (!calc) return fmt(amount);
  return getLocalizedText(
    calc.isAverage
      ? "settlement.summary.card.provision_calc_average"
      : "settlement.summary.card.provision_calc",
    {
      months: formatMonthCount(calc.months),
      monthly: fmt(calc.perMonth),
      amount: fmt(amount),
    },
  );
};

// One provision of a tenant's kind as a row of the provisions' "i".
const kindProvisionRow = (
  provision: ISettlementKindProvision,
  lease?: ContractRef,
): IInfoRow => {
  if (provision.source === "added") {
    return {
      label:
        provision.title?.trim() ||
        getLocalizedText("settlement.provisions.extra_fallback_title"),
      value: fmt(provision.amount),
    };
  }
  const value = provisionValue({
    amount: provision.amount,
    months: provision.months,
    monthly: lease?.provisionLines?.find(line => line.key === provision.key)
      ?.monthly,
  });
  if (provision.lineKind === "additional") {
    return {
      label:
        provision.title?.trim() ||
        getLocalizedText("settlement.provisions.line.additional_fallback"),
      value: getLocalizedText("settlement.summary.card.in_private_kind", {
        value,
      }),
    };
  }
  const slice: ProvisionSlice =
    provision.lineKind === "common"
      ? "common"
      : provision.lineKind === "nonCommon"
        ? "private"
        : provision.slice ?? "common";
  return { label: sliceName(slice), value };
};

// One line per kind (Lewis, 2026-10-17): "Saldo gemeenschappelijk · € 467,24
// terug", then "Saldo privaat · € 94,80 terug".
const kindBalanceRow = (kind: ISettlementKind): IInfoRow => ({
  label:
    kind.key === COMMON_KIND
      ? getLocalizedText("settlement.summary.card.balance_common")
      : kind.key === PRIVATE_KIND
        ? getLocalizedText("settlement.summary.card.balance_private")
        : `${getLocalizedText("settlement.summary.kind.balance")} ${kindLabel(
            kind,
          )}`,
  value: kindBalanceText(kind.balance, fmt),
});

// The rows of the provisions' "i": each provision line (per kind of cost,
// the lease's lines and the ones added by hand), the
// period, the balance per kind and what was billed outside the lease.
// Older snapshots without the kinds list the lease's slices or its one
// total, and the provisions added by hand.
const provisionInfoRows = (params: {
  pc: PerContractResult;
  lease?: ContractRef;
  period: { from: string; to: string };
  contract?: { from: string; to: string };
}): IInfoRow[] => {
  const { pc, lease, period, contract } = params;
  const kinds =
    pc.kinds &&
    pc.costVat != null &&
    kindsAddUp({
      kinds: pc.kinds,
      cost: pc.cost,
      costVat: pc.costVat,
      provision: pc.provision,
    })
      ? pc.kinds
      : undefined;
  const sliceLines = kinds ? undefined : provisionSliceLines(pc);
  const leaseRows: IInfoRow[] = kinds
    ? kinds.flatMap(kind =>
        kind.provisions.map(provision => kindProvisionRow(provision, lease)),
      )
    : [
        ...(sliceLines
          ? sliceLines.map(line => ({
              label: sliceName(line.slice),
              value: provisionValue({
                amount: line.amount,
                months: line.months,
                monthly:
                  line.slice === "common"
                    ? lease?.monthlyProvisionCommon
                    : lease?.monthlyProvisionPrivate,
              }),
            }))
          : Math.abs(leaseProvisionOf(pc)) > 0.005 ||
              (pc.extraProvisions ?? []).length === 0
            ? [
                {
                  label: getLocalizedText(
                    "settlement.summary.contract_provisions",
                  ),
                  value: fmt(leaseProvisionOf(pc)),
                },
              ]
            : []),
        ...(pc.extraProvisions ?? []).map(extra => ({
          label: extraProvisionTitle(extra),
          value: fmt(extra.amount),
        })),
      ];
  const periodFrom =
    contract?.from && contract.from > period.from ? contract.from : period.from;
  const periodTo =
    contract?.to && contract.to < period.to ? contract.to : period.to;
  const intervals = pc.provisionIntervals?.length
    ? pc.provisionIntervals
    : [
        periodFrom <= periodTo
          ? { from: periodFrom, to: periodTo }
          : { from: period.from, to: period.to },
      ];
  const billedOutside = pc.provisionBilledOutsideContract ?? 0;
  return [
    ...leaseRows,
    {
      label: getLocalizedText("costs.field.period"),
      value: describeProvisionPeriods(intervals),
    },
    ...(kinds && kinds.length > 1 ? kinds.map(kindBalanceRow) : []),
    // Information only: provision billed for days outside the lease is not
    // part of the balance (no refund flow, C.12).
    ...(billedOutside > 0
      ? [
          {
            label: getLocalizedText("settlement.summary.card.billed_outside"),
            value: getLocalizedText(
              "settlement.summary.card.billed_outside_value",
              { amount: fmt(billedOutside) },
            ),
          },
        ]
      : []),
  ];
};

// The statement under a card, as a Rentio card's footer action: an outline
// button "Overzicht downloaden" (Lewis, 2026-10-19).
const StatementButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <StatementAction>
    <Button appearance="outline" onClick={onClick}>
      {getLocalizedText("settlement.summary.download_statement")}
    </Button>
  </StatementAction>
);

// "Rest van bus 6 (61 dagen): …" per invoice, under the costs' figures in
// their "i", labelled with the invoice.
const restInfoRows = (sections: ICardSection[]): IInfoRow[] =>
  sections.flatMap(section =>
    section.rows.flatMap(row =>
      row.rest ? [{ label: row.name, value: row.rest }] : [],
    ),
  );

// A tenant's share of one invoice incl. VAT, as the engine charged it, or
// undefined when the result does not say (an older snapshot).
const tenantInvoiceShare = (
  pc: PerContractResult,
  invoiceId: string,
): number | undefined => {
  const excl = pc.costByInvoice?.[invoiceId];
  const vat = pc.vatByInvoice?.[invoiceId];
  return excl == null || vat == null ? undefined : roundCents(excl + vat);
};

const sumCents = (amounts: number[]): number =>
  roundCents(amounts.reduce((sum, amount) => sum + amount, 0));

// The VAT per rate in the "i" of a card's "BTW" (Lewis, 2026-10-19): Tarief,
// Excl. btw, Btw, one line per rate on the card, a bold total under two or
// more. Amounts in the financial font, as everywhere.
const VatRateTable = styled.table`
  border-spacing: 0;
  text-align: right;
  th,
  td {
    padding: 2px 0 2px 16px;
    white-space: nowrap;
  }
  th:first-child,
  td:first-child {
    padding-left: 0;
    text-align: left;
  }
  th {
    font-weight: ${FontWeights.Regular};
    color: ${({ theme }) => theme.colors.neutral40};
  }
  tr.total td {
    padding-top: 4px;
    border-top: 1px solid ${({ theme }) => theme.colors.neutral40};
    font-weight: ${FontWeights.SemiBold};
  }
`;

const rateLabel = (rate?: VatValue): string => {
  if (rate == null) {
    return getLocalizedText("settlement.summary.card.vat_rate_other");
  }
  return typeof rate === "number"
    ? getLocalizedText("settlement.summary.card.vat_rate_pct", {
        pct: formatWeight(rate),
      })
    : vatLabel(rate);
};

const VatRates: React.FC<{
  lines: IVatRateLine[];
  total: { excl: number; vat: number };
}> = ({ lines, total }) => (
  <VatRateTable>
    <thead>
      <tr>
        <th>{getLocalizedText("settlement.summary.card.vat_rate")}</th>
        <th>{getLocalizedText("costs.detail.excl_vat")}</th>
        <th>{getLocalizedText("costs.modal.vat")}</th>
      </tr>
    </thead>
    <tbody>
      {lines.map(line => (
        <tr key={rateLabel(line.rate)}>
          <td>{rateLabel(line.rate)}</td>
          <td>
            <Money amount={line.excl} />
          </td>
          <td>
            <Money amount={line.vat} />
          </td>
        </tr>
      ))}
      {lines.length > 1 && (
        <tr className="total">
          <td>{getLocalizedText("costs.field.total")}</td>
          <td>
            <Money amount={total.excl} variation="strong" />
          </td>
          <td>
            <Money amount={total.vat} variation="strong" />
          </td>
        </tr>
      )}
    </tbody>
  </VatRateTable>
);

// A row's share excl. VAT, its VAT and its rate, for the VAT per rate.
const vatRateRowsOf = (sections: ICardSection[]) =>
  sections
    .flatMap(section => section.rows)
    .map(row => ({
      excl: fromCents(toCents(row.amount) - toCents(row.amountVat ?? 0)),
      vat: row.amountVat ?? 0,
      rate: row.vatRate,
    }));

// The first two totals under a card (Lewis, 2026-10-19): its costs excl.
// VAT, and their VAT with its split per rate in the "i". The rates come
// from the rows of the table above, which add up to both totals, so the
// lines do too. The confirmed settlement's "Kosten" uses them as well.
export const costVatTotals = (params: {
  excl: number;
  vat: number;
  vatRows: IVatRateRow[];
}): ICardTotal[] => {
  const { lines, total } = vatByRate(params.vatRows);
  return [
    {
      id: "costs_excl",
      label: getLocalizedText("settlement.summary.card.total_costs_excl"),
      amount: params.excl,
    },
    {
      id: "vat",
      label: getLocalizedText("settlement.summary.card.total_vat"),
      ...(lines.length > 0
        ? {
            info: {
              rows: [],
              content: <VatRates lines={lines} total={total} />,
              label: lines
                .map(
                  line =>
                    `${rateLabel(line.rate)}: ${fmt(line.excl)}, ${fmt(
                      line.vat,
                    )}`,
                )
                .join(". "),
            },
          }
        : {}),
      amount: params.vat,
    },
  ];
};

// A tenant's costs as their card adds them up: per category, the engine's
// figure excl. VAT and its VAT (the category's average rate only for a
// snapshot frozen before the VAT per category existed).
const categoryVatOf = (params: {
  pc: PerContractResult;
  catMeta: Record<string, CatMeta>;
  key: string;
  excl: number;
}): number => {
  const { pc, catMeta, key, excl } = params;
  const meta = catMeta[key];
  const rate = meta && meta.excl > 0 ? meta.btw / meta.excl : 0;
  return pc.breakdownVat?.[key] ?? roundCents(excl * rate);
};

// A tenant card's costs incl. VAT, the figure its balance starts from.
export const tenantCardCosts = (
  pc: PerContractResult,
  catMeta: Record<string, CatMeta>,
): number => {
  const parts = Object.entries(pc.breakdown).filter(
    ([, excl]) => Math.abs(excl) > 0.001,
  );
  const excl = roundCents(parts.reduce((sum, [, value]) => sum + value, 0));
  const vat = roundCents(
    parts.reduce(
      (sum, [key, value]) =>
        sum + categoryVatOf({ pc, catMeta, key, excl: value }),
      0,
    ),
  );
  return roundCents(excl + vat);
};

// The last two totals of a tenant's card: the provisions, then the bold
// line that names who pays whom (Lewis, 2026-10-19; its own texts: the
// drawer's "Terug te geven" lozenge keeps its short label). The confirmed
// settlement's "Kosten" closes with them too when it holds one lease.
export const tenantClosingTotals = (params: {
  pc: PerContractResult;
  // The tenant's costs incl. VAT (tenantCardCosts).
  costs: number;
  lease?: ContractRef;
  period: { from: string; to: string };
  contract?: { from: string; to: string };
}): ICardTotal[] => {
  const { pc, costs, lease, period, contract } = params;
  const balance = roundCents(costs - pc.provision);
  // A tenant with nothing to settle reads the same, whatever the reason
  // (a forfait is never named in a settlement; Lewis, 2026-10-20).
  const finalLabel =
    balance > 0.005
      ? getLocalizedText("settlement.summary.card.to_pay")
      : balance < -0.005
        ? getLocalizedText("settlement.summary.card.to_refund")
        : getLocalizedText("costs.settlement.status.nothing_to_pay");
  return [
    {
      id: "provisions",
      label: getLocalizedText("settlement.summary.kind.provisions"),
      info: { rows: provisionInfoRows({ pc, lease, period, contract }) },
      amountText: signedProvision(pc.provision),
    },
    {
      id: "balance",
      label: finalLabel,
      amount: Math.abs(balance) > 0.005 ? Math.abs(balance) : undefined,
      isFinal: true,
    },
  ];
};

export const SummaryCard: React.FC<{
  variant: TCardVariant;
  title: React.ReactNode;
  // The settlement's period.
  period: { from: string; to: string };
  pc: PerContractResult;
  catMeta: Record<string, CatMeta>;
  // The wizard's rows open their cost (the cost drawer with this
  // settlement's split), with the party whose row it was; the confirmed
  // card's rows open nothing (Lewis, 2026-10-19).
  onOpenCost?: (invoiceId: string, party?: ICostSplitParty) => void;
  // Optional: only the saved settlement (ConfirmedView) offers the PDF; the
  // wizard summary omits it, so the button renders only when provided.
  onOpenStatement?: () => void;
  // Optional: the tenant's rental-contract period: a row's occupied days
  // and the provisions' period are measured against it.
  contract?: { from: string; to: string };
  // The lease, for its provision lines' monthly amounts.
  lease?: ContractRef;
  // How each category was split, for the wizard's "Aandeel" and the
  // confirmed card's "i".
  distribution?: ICardDistribution;
  // The whole settlement's figures: the readings, and who carries the rest
  // of each invoice (see costShareCalculation).
  shareContext?: ICostShareContext;
}> = ({
  variant,
  title,
  period,
  pc,
  catMeta,
  onOpenCost,
  onOpenStatement,
  contract,
  lease,
  distribution,
  shareContext,
}) => {
  const categories = Object.entries(pc.breakdown)
    .filter(([, v]) => Math.abs(v) > 0.001)
    .sort(([a], [b]) => compareBreakdownKeys(a, b))
    .map(([key, excl]) => {
      const meta = catMeta[key];
      // The engine's per-cost VAT (what the payment and the PDF use); the
      // category average only for snapshots frozen before it existed.
      const vat = categoryVatOf({ pc, catMeta, key, excl });
      const allCosts = meta?.costs ?? [];
      // The costs this tenant has a share in; older snapshots do not say,
      // so all of the category's.
      const withShare = pc.costByInvoice
        ? allCosts.filter(inv => pc.costByInvoice?.[inv.id] != null)
        : [];
      const costs = withShare.length > 0 ? withShare : allCosts;
      return {
        key,
        excl,
        vat,
        amount: roundCents(excl + vat),
        costs,
        isPrivate: isUnitBreakdownKey(key),
      };
    });

  // The days each kind of the lease was a forfait (N71): its rows count
  // only the other days, as its amounts do.
  const fixedFeeDaysBySlice = fixedFeeDaysOfLease(
    lease,
    pc.forfaitIntervalsBySlice,
  );
  const blocks: ICardBlock[] = categories.flatMap(category => {
    const { key, costs } = category;
    const meta = catMeta[key];
    const label = rowCategoryLabel(key, meta);
    const perOccupant = distribution?.byCat[key]?.perOccupant;
    const fixedFeeDays =
      fixedFeeDaysBySlice[category.isPrivate ? "private" : "common"];
    const shares = costs.map(inv => tenantInvoiceShare(pc, inv.id));
    const vats = costs.map(inv => pc.vatByInvoice?.[inv.id]);
    // One row per invoice when the engine's figures per invoice add up to
    // the category's, its VAT too; one row of the category's own figure
    // otherwise.
    const isPerInvoice =
      costs.length > 0 &&
      shares.every(share => share != null) &&
      sumCents(shares as number[]) === category.amount &&
      vats.every(vat => vat != null) &&
      sumCents(vats as number[]) === roundCents(category.vat);
    if (!isPerInvoice) {
      const occupied = occupiedDaysOf({
        costs,
        contract,
        perOccupant,
        fixedFeeDays,
      });
      return [
        {
          key,
          rows: [
            {
              id: key,
              title: label,
              sub: suppliersText(costs),
              name: label,
              period: costsPeriod(costs, contract),
              vatRate: commonVatRate(costs),
              split: tenantSplit({
                key,
                costs,
                pc,
                distribution,
                split: shareContext?.split,
              }),
              occupied,
              info: () =>
                tenantAmountInfo({
                  key,
                  costs,
                  pc,
                  distribution,
                  split: shareContext?.split,
                  occupied,
                }),
              amount: category.amount,
              amountVat: roundCents(category.vat),
              invoiceId: costs[0]?.id,
            },
          ],
        },
      ];
    }
    return costs.map((inv, index) => {
      const amount = shares[index] as number;
      const occupied = occupiedDaysOf({
        costs: [inv],
        contract,
        perOccupant,
        fixedFeeDays,
      });
      return {
        key,
        date: invoiceDate(inv),
        rows: [
          {
            id: inv.id,
            title: invoiceCategory(key, inv),
            vatRate: inv.vatPct,
            sub: invoiceSub(key, inv),
            name: invoiceDescription(inv, label),
            period: { from: inv.periodFrom, to: inv.periodTo },
            value: invoiceValue(inv),
            valueVat: btwOf(inv),
            split: tenantSplit({
              key,
              costs: [inv],
              pc,
              distribution,
              split: shareContext?.split,
            }),
            occupied,
            info: () =>
              tenantAmountInfo({
                key,
                costs: [inv],
                pc,
                distribution,
                split: shareContext?.split,
                occupied,
                amountExcl: pc.costByInvoice?.[inv.id],
              }),
            amount,
            amountVat: vats[index] as number,
            invoiceId: inv.id,
            rest: shareContext
              ? costShareCalculation({
                  row: {
                    party: "tenant",
                    key,
                    contractId: pc.contractId,
                    amount,
                    onlyInvoiceId: inv.id,
                  },
                  context: shareContext,
                }).rest
              : undefined,
          },
        ],
      };
    });
  });
  const sections = cardSections(blocks);

  const totExcl = roundCents(categories.reduce((s, r) => s + r.excl, 0));
  const totVat = roundCents(categories.reduce((s, r) => s + r.vat, 0));
  const invoices = roundCents(totExcl + totVat);

  const costsInfoRow = (label: string, part: typeof categories): IInfoRow[] =>
    part.length === 0
      ? []
      : [
          {
            label,
            value: costCountText(
              sumCents(part.map(r => r.amount)),
              part.reduce((sum, r) => sum + r.costs.length, 0),
            ),
          },
        ];
  // The rows' excl. VAT and VAT add up to these to the cent: each row is
  // its category's figure, or invoices that add up to it, VAT included.
  const totals: ICardTotal[] = [
    ...costVatTotals({
      excl: totExcl,
      vat: totVat,
      vatRows: vatRateRowsOf(sections),
    }),
    {
      id: "costs",
      label: getLocalizedText("settlement.summary.card.total_costs_incl"),
      info: {
        rows: [
          ...costsInfoRow(
            costKindLabel("common"),
            categories.filter(r => !r.isPrivate),
          ),
          ...costsInfoRow(
            costKindLabel("private"),
            categories.filter(r => r.isPrivate),
          ),
          ...restInfoRows(sections),
        ],
        note:
          categories.length > 0
            ? getLocalizedText("settlement.summary.card.invoices_note")
            : undefined,
      },
      amount: invoices,
    },
    ...tenantClosingTotals({ pc, costs: invoices, lease, period, contract }),
  ];
  // A confirmed card whose lease end moved since (Y11): what the next
  // settlement corrects.
  const leaseEndMoved =
    variant === "confirmed" && pc.leaseEnd
      ? settledLeaseEndChange({
          leaseEnd: pc.leaseEnd,
          lease,
          lastDay: (pc.provisionIntervals ?? []).reduce(
            (last, iv) => (iv.to > last ? iv.to : last),
            period.to,
          ),
        })
      : undefined;

  return (
    <CollapsibleCard
      title={title}
      subtitle={
        leaseEndMoved && (
          <TextStyle
            variation={
              leaseEndMoved.change.kind === "earlier" ? "warn" : "subdued"
            }
            size="small"
          >
            {getLocalizedText(LEASE_END_CARD_KEYS[leaseEndMoved.change.kind], {
              until: fmtDate(leaseEndMoved.until),
              ...(leaseEndMoved.change.kind === "open"
                ? {}
                : { end: fmtDate(leaseEndMoved.change.end) }),
            })}
          </TextStyle>
        )
      }
      totals={
        <>
          <CardTotals totals={totals} />
          {onOpenStatement && <StatementButton onClick={onOpenStatement} />}
        </>
      }
    >
      <CardTable
        variant={variant}
        sections={sections}
        amountHeading={getLocalizedText(
          "settlement.summary.card.column_to_pay",
        )}
        onOpenCost={
          onOpenCost &&
          (invoiceId => onOpenCost(invoiceId, { contractId: pc.contractId }))
        }
      />
    </CollapsibleCard>
  );
};

// The owner's own settlement card, in the same table as a tenant's: one row
// per invoice under its category, its cause ("Vast eigenaarsdeel (10%)",
// "Leegstand"), the share of the owner's units and the days a unit stood
// empty (in the wizard's columns, or with the cause and the whole sum in
// the confirmed card's "i"); a difference with a departure settlement
// (F85) on a row of its own. Then the bold total with its breakdown in the
// "i". The owner never pays provisions.
// One cause of an owner's part of a cost, as its row shows it.
interface IOwnerCauseRow {
  cause: "base" | "vacancy";
  title: React.ReactNode;
  split: React.ReactNode;
  occupied?: IOccupiedDays;
  info: IInfoTipRow[];
  amount: number;
  amountVat: number;
}

// "Vast eigenaarsdeel (10%)" and "Leegstand (huurdersdeel)": the cause of
// an owner's row, what its part in brackets means in that part's hover
// (Lewis, 2026-10-19). Without a known owner percentage the fixed share is
// named alone, and the vacancy's hover names no percentage.
const CauseLabel: React.FC<{
  cause: "base" | "vacancy";
  ownerPct?: number;
}> = ({ cause, ownerPct }) => {
  const hint = (part: string, help: string) => (
    <Tooltip tooltipContent={help}>
      <span style={{ cursor: "help" }}>{part}</span>
    </Tooltip>
  );
  if (cause === "base") {
    const label = getLocalizedText("settlement.summary.card.cause_fixed");
    if (ownerPct == null) return <>{label}</>;
    const pct = formatWeight(ownerPct);
    return (
      <>
        {label}{" "}
        {hint(
          getLocalizedText("settlement.summary.card.cause_fixed_pct", { pct }),
          getLocalizedText("settlement.summary.card.cause_fixed_help", {
            pct,
          }),
        )}
      </>
    );
  }
  return (
    <>
      {getLocalizedText("settlement.share_card.vacancy")}{" "}
      {hint(
        getLocalizedText("settlement.summary.card.cause_vacancy_part"),
        ownerPct == null
          ? getLocalizedText(
              "settlement.summary.card.cause_vacancy_help_no_pct",
            )
          : getLocalizedText("settlement.summary.card.cause_vacancy_help", {
              pct: formatWeight(100 - ownerPct),
            }),
      )}
    </>
  );
};

// An owner's part that is no fixed share (M29): a correction of a lease
// end, or the part of a reading's cost no reading accounts for.
const neutralCauseText = (cause: Exclude<TOwnerBaseCause, "fixed">): string =>
  getLocalizedText(
    cause === "credited"
      ? "settlement.summary.card.owner_credit"
      : "settlement.distribution.owner_bears",
  );

// "Hugo Huurder: huurde langer: 01/07/2026 – 31/07/2026 (31 dagen)", in
// the "i" of an owner's correction row (M29).
const correctionInfoRows = (
  correction: ReturnType<typeof ownerCorrectionOf>,
): IInfoTipRow[] =>
  correction
    ? [
        {
          label: correction.tenantName,
          value: getLocalizedText(
            correction.isLonger
              ? "settlement.summary.card.correction_longer"
              : "settlement.summary.card.correction_shorter",
            {
              from: fmtDate(correction.from),
              to: fmtDate(correction.to),
              days: String(correction.days),
            },
          ),
        },
      ]
    : [];

// "Vast eigenaarsdeel: 10%", in the "i" of an owner's row; none without a
// known owner percentage.
const fixedShareRows = (ownerPct?: number): IInfoTipRow[] =>
  ownerPct == null
    ? []
    : [
        {
          label: getLocalizedText("settlement.summary.card.cause_fixed"),
          value: getLocalizedText("settlement.summary.card.split_pct", {
            pct: formatWeight(ownerPct),
          }),
        },
      ];

// "Leegstand: 92/92 dagen (…)", in the "i" of an owner's row: the days
// the unit stood empty, or with several such units what the vacancy means
// and each unit with its days under it.
const vacancyRow = (params: {
  days?: IOccupiedDays;
  ownerPct?: number;
  units?: { label: string; days: IOccupiedDays }[];
}): IInfoTipRow => {
  const { days, ownerPct, units = [] } = params;
  return {
    label: getLocalizedText("settlement.share_card.vacancy"),
    value: days
      ? occupiedDaysText(days)
      : ownerPct == null
        ? getLocalizedText("settlement.summary.card.cause_vacancy_help_no_pct")
        : getLocalizedText("settlement.summary.card.cause_vacancy_help", {
            pct: formatWeight(100 - ownerPct),
          }),
    details: days
      ? undefined
      : units.map(unit => ({
          label: unit.label,
          value: occupiedDaysText(unit.days),
        })),
  };
};

export const OwnerSummaryCard: React.FC<{
  variant: TCardVariant;
  title: React.ReactNode;
  baseByCat: Record<string, number>;
  vacancyByCat: Record<string, number>;
  pctByCat: Record<string, number>;
  // VAT the owner carries per category, summed by the engine at each cost's
  // own rate. Falls back to the category average for older frozen snapshots.
  vatByCat?: Record<string, number>;
  catMeta: Record<string, CatMeta>;
  // See SummaryCard: the wizard's rows only.
  onOpenCost?: (invoiceId: string, party?: ICostSplitParty) => void;
  // Optional — see SummaryCard: only the saved settlement shows the PDF button.
  onOpenStatement?: () => void;
  // Per consumption or metering-company category: the key that split the
  // owners' part over the units (see ownerPartKeyName).
  ownerPartKeyByCat?: Record<string, string>;
  // The owner's departure differences per category (departureDiffsByCat of
  // the owner's lines), for the "i". Absent for owners frozen before they
  // existed: the difference then stays in the fixed share.
  departureDiffsByCat?: Record<string, OwnerDepartureDiff[]>;
  // The split of each category and the owner's units: their share of the
  // key that split the owners' part ("2/3"). Without them that key is named.
  distribution?: ICardDistribution;
  unitIds?: string[];
  // The owner's part per invoice; absent on older snapshots, which show one
  // row per category and part.
  lines?: OwnerCostLine[];
  // See SummaryCard.
  shareContext?: ICostShareContext;
  // The corrections of lease ends that moved (decision 3), for the "i" of
  // the owner's correction rows (M29).
  departureCorrections?: IDepartureCorrection[];
}> = ({
  variant,
  title,
  baseByCat,
  vacancyByCat,
  pctByCat,
  vatByCat,
  catMeta,
  onOpenCost,
  onOpenStatement,
  ownerPartKeyByCat,
  departureDiffsByCat: diffsByCat,
  distribution,
  unitIds,
  lines,
  shareContext,
  departureCorrections,
}) => {
  // A unit as the key's units name it: its full address.
  const unitLabelOf = (unitId: string): string => {
    const unit = distribution?.units.find(u => String(u.id) === unitId);
    return unit?.address ?? unit?.code ?? unitId;
  };
  // The unit's share of a cost on an owner's row (Lewis, 2026-10-19): the
  // ratio of the owner's units in the key that split the cost, the key and
  // the units in its hover (the wizard's cell), or with the key's name (the
  // confirmed card's "i"), as on a tenant's card; for a reading or a metering
  // company's amount the key that split the owners' part; a dash, and no
  // line, for a unit's own cost. Never the owner's percentage: the cause
  // says it.
  const shareOf = (
    key: string,
    costPeriods: { from: string; to: string }[],
    ids?: string[],
  ): {
    split: React.ReactNode;
    rows: IInfoTipRow[];
    factor?: ICalculationFactor;
  } => {
    if (isUnitBreakdownKey(key)) return { split: <Dash />, rows: [] };
    const dist = distribution?.byCat[key];
    const ownerPartKey = ownerPartKeyByCat?.[key];
    const keyText = ownerPartKey
      ? getLocalizedText("settlement.distribution.owner_part_key", {
          name: keyDisplayName(ownerPartKey),
        })
      : undefined;
    const keyDef = dist?.key ?? dist?.ownerPartKey;
    if (keyDef && distribution && ids && ids.length > 0) {
      const shares = keySharesOf({
        keyDef,
        unitIds: ids,
        distribution,
        costPeriods,
      });
      return {
        split: (
          <KeySplit
            keyDef={keyDef}
            unitIds={ids}
            distribution={distribution}
            costPeriods={costPeriods}
            title={dist?.key ? undefined : keyText}
          />
        ),
        rows: keyShareRows(keyDisplayName(keyDef.name), shares),
        factor: keyShareFactor(shares),
      };
    }
    return {
      split: keyText ?? <Dash />,
      rows: keyText ? [{ label: shareLabel(), value: keyText }] : [],
    };
  };
  const kindTag = (row: IOwnerCardRow): string => {
    if (row.kind === "base") {
      return getLocalizedText("settlement.summary.card.tag_fixed_share");
    }
    if (row.kind === "vacancy") {
      return getLocalizedText("settlement.summary.card.tag_vacancy");
    }
    return getLocalizedText("settlement.summary.owner_departure_difference", {
      tenant: row.departure?.tenantName ?? "",
    });
  };
  // The owner's figures per category and part, as the engine summed them:
  // the "i" and the rows of an older snapshot. Exact VAT from the engine
  // when available; the category average is only a fallback for snapshots
  // frozen before vatByCat existed.
  const categoryRows = ownerCardRows({
    baseByCat,
    vacancyByCat,
    vatByCat,
    departureDiffsByCat: diffsByCat,
    fallbackRateOf: key => {
      const meta = catMeta[key];
      return meta && meta.excl > 0 ? meta.btw / meta.excl : 0;
    },
  }).map(row => ({ ...row, amount: roundCents(row.excl + row.btw) }));
  const keys = [...new Set(categoryRows.map(row => row.key))];

  // One owner line of an invoice: its own part, then each difference with
  // a departure settlement on a row of its own.
  const lineRows = (
    key: string,
    line: OwnerCostLine,
    inv: Invoice | undefined,
    label: string,
  ): ICardRow[] => {
    const diffs = line.departureDiffs ?? [];
    // The line's own part, and the same per cause (fixed share, vacancy).
    const causes = ownerLineCauses(line);
    const { amount, vat: amountVat } = causes.total;
    const period = inv ? { from: inv.periodFrom, to: inv.periodTo } : undefined;
    const costPeriods = period ? [period] : [];
    const base = {
      period,
      value: inv ? invoiceValue(inv) : undefined,
      valueVat: inv ? btwOf(inv) : undefined,
      invoiceId: line.invoiceId,
      vatRate: inv?.vatPct,
    };
    const name = inv ? invoiceDescription(inv, label) : label;
    const calculation =
      shareContext && diffs.length === 0
        ? costShareCalculation({
            row: {
              party: "owner",
              key,
              kind: causes.base ? "base" : "vacancy",
              unitIds,
              amount,
              onlyInvoiceId: line.invoiceId,
            },
            context: shareContext,
          })
        : undefined;
    // The owner's units of the cost: the unit of its own cost, else all.
    const ownUnitIds = line.unitId ? [String(line.unitId)] : unitIds;
    // Those that stood empty, and their days when one did.
    const vacancy =
      causes.vacancy && inv && shareContext
        ? ownerVacancyOf({
            key,
            invoice: inv,
            unitIds: ownUnitIds ?? [],
            context: shareContext,
          })
        : undefined;
    const ownerPct = line.ownerPct ?? pctByCat[key];
    // The whole sum of a cause, from the cost to its part excl. VAT.
    const sumRows = (
      part: { amount: number; vat: number },
      factors: (ICalculationFactor | undefined)[],
    ): IInfoTipRow[] =>
      inv
        ? calculationRows({
            base: inv.amountExclVat,
            factors,
            amount: fromCents(toCents(part.amount) - toCents(part.vat)),
          })
        : [];
    const baseShare = shareOf(key, costPeriods, ownUnitIds);
    const vacancyShare = shareOf(
      key,
      costPeriods,
      vacancy?.unitIds ?? ownUnitIds,
    );
    const vacantDays = vacancy?.days
      ? { ...vacancy.days, period, isVacant: true }
      : undefined;
    // Each empty unit's weight and days, the vacancy's own factor: its
    // part over several units is a sum of their products.
    const vacantUnits = (vacancy?.daysByUnit ?? []).map(unit => ({
      unitId: unit.unitId,
      days: { days: unit.days, total: unit.total, isVacant: true },
    }));
    const vacancyFactor = sumFactor(
      vacantUnits.map(unit => [
        ...(isUnitBreakdownKey(key)
          ? []
          : [shareOf(key, costPeriods, [unit.unitId]).factor]),
        daysFactor(unit.days),
      ]),
    );
    // A reading's or a metering company's category: its owner part is no
    // fixed share, and its vacancy is a reading, not a key's share (M29).
    const keyDist = distribution?.byCat[key];
    const isByReadings =
      !isUnitBreakdownKey(key) &&
      !keyDist?.key &&
      (keyDist?.method === "consumption" || keyDist?.method === "company");
    const baseCause = causes.base
      ? ownerBaseCauseOf({
          key,
          amount: causes.base.amount,
          ownerPct,
          isSplitByReadings: isByReadings,
        })
      : "fixed";
    const correction = ownerCorrectionOf({
      key,
      corrections: departureCorrections,
    });
    const readingText =
      causes.vacancy && inv && shareContext && isByReadings
        ? ownerReadingVacancyText({
            key,
            invoice: inv,
            unitIds: ownUnitIds ?? [],
            line,
            context: shareContext,
          })
        : undefined;
    const causeRows: IOwnerCauseRow[] = [
      ...(causes.base
        ? [
            {
              cause: "base" as const,
              title:
                baseCause === "fixed" ? (
                  <CauseLabel cause="base" ownerPct={ownerPct} />
                ) : (
                  neutralCauseText(baseCause)
                ),
              split: correction ? <Dash /> : baseShare.split,
              info: correction
                ? correctionInfoRows(correction)
                : [
                    ...baseShare.rows,
                    ...(baseCause === "fixed" ? fixedShareRows(ownerPct) : []),
                    ...sumRows(causes.base, [
                      baseShare.factor,
                      pctFactor(ownerPct),
                    ]),
                  ],
              amount: causes.base.amount,
              amountVat: causes.base.vat,
            },
          ]
        : []),
      ...(causes.vacancy
        ? [
            {
              cause: "vacancy" as const,
              title: isByReadings ? (
                getLocalizedText("settlement.share_card.vacancy")
              ) : (
                <CauseLabel cause="vacancy" ownerPct={ownerPct} />
              ),
              split: readingText ?? vacancyShare.split,
              occupied: vacantDays,
              info: [
                ...(readingText
                  ? [{ label: shareLabel(), value: readingText }]
                  : vacancyShare.rows),
                vacancyRow({
                  days: vacantDays,
                  ownerPct,
                  units: vacantUnits.map(unit => ({
                    label: unitLabelOf(unit.unitId),
                    days: unit.days,
                  })),
                }),
                ...sumRows(causes.vacancy, [
                  vacancyFactor,
                  pctFactor(ownerPct == null ? undefined : 100 - ownerPct),
                ]),
              ],
              amount: causes.vacancy.amount,
              amountVat: causes.vacancy.vat,
            },
          ]
        : []),
    ];
    const id = `${key}::${line.invoiceId}`;
    const costRow = {
      ...base,
      id,
      title: inv ? invoiceCategory(key, inv) : label,
      sub: inv ? invoiceSub(key, inv) : undefined,
      name,
      rest: calculation?.rest,
    };
    const rows: ICardRow[] = [];
    if (diffs.length === 0 || Math.abs(amount) >= 0.005) {
      if (causeRows.length === 2) {
        // Both causes: the cost's row, then a row per cause under it.
        rows.push({ ...costRow, layout: "costHead", split: null, amount: 0 });
        causeRows.forEach(({ cause, ...causeRow }) =>
          rows.push({
            ...base,
            ...causeRow,
            id: `${id}::${cause}`,
            layout: "cause",
            name,
          }),
        );
      } else {
        // One cause: the cost's row, the cause small under it.
        const [only] = causeRows;
        rows.push({
          ...costRow,
          cause: only?.title,
          split: only?.split ?? <Dash />,
          occupied: only?.occupied,
          info: only?.info,
          amount,
          amountVat,
        });
      }
    }
    for (const diff of diffs) {
      const diffLabel = getLocalizedText(
        "settlement.summary.owner_departure_difference",
        { tenant: diff.tenantName },
      );
      rows.push({
        ...base,
        id: `${key}::${line.invoiceId}::${diff.settlementId}`,
        title: diffLabel,
        sub: name,
        name: diffLabel,
        split: <Dash />,
        amount: roundCents(diff.amount + diff.vat),
        amountVat: diff.vat,
      });
    }
    return rows;
  };

  const blocks: ICardBlock[] = keys.flatMap(key => {
    const meta = catMeta[key];
    const label = rowCategoryLabel(key, meta);
    const keyLines = (lines ?? []).filter(line => line.label === key);
    const invoiceBlocks: ICardBlock[] = keyLines.map(line => {
      const inv = meta?.costs.find(cost => cost.id === line.invoiceId);
      return {
        key,
        date: invoiceDate(inv),
        rows: lineRows(key, line, inv, label),
      };
    });
    const keyRows = categoryRows.filter(row => row.key === key);
    const categoryTotal = sumCents(keyRows.map(row => row.amount));
    const categoryVat = sumCents(keyRows.map(row => row.btw));
    // One row per invoice when the owner's lines add up to the category's
    // figures, its VAT too; one row per part otherwise (an older snapshot).
    const invoiceRows = invoiceBlocks.flatMap(block => block.rows);
    const isPerInvoice =
      invoiceRows.length > 0 &&
      sumCents(invoiceRows.map(row => row.amount)) === categoryTotal &&
      sumCents(invoiceRows.map(row => row.amountVat ?? 0)) === categoryVat;
    if (isPerInvoice) return invoiceBlocks;
    return [
      {
        key,
        rows: categoryRows
          .filter(row => row.key === key)
          .map(row => {
            const share =
              row.kind === "departure_difference"
                ? undefined
                : shareOf(key, meta?.costPeriods ?? [], unitIds);
            return {
              id: `${row.key}::${row.kind}::${
                row.departure?.settlementId ?? ""
              }`,
              title: label,
              vatRate: commonVatRate(meta?.costs ?? []),
              sub: [kindTag(row), suppliersText(meta?.costs ?? [])]
                .filter(Boolean)
                .join(" · "),
              name: label,
              period: costsPeriod(meta?.costs ?? []),
              split: share?.split ?? <Dash />,
              info: share?.rows,
              amount: row.amount,
              amountVat: row.btw,
              invoiceId: meta?.invoiceIds[0],
            };
          }),
      },
    ];
  });
  const sections = cardSections(blocks);

  // Rounded to the cent: the rows follow the stored key order, and summing
  // floats in another order showed 4.188,480000000001.
  const total = sumCents(
    sections.flatMap(section => section.rows.map(row => row.amount)),
  );
  // The rows' own VAT, so their excl. VAT and VAT add up to the totals to
  // the cent: the VAT a row shows is its rounded figure.
  const totVat = fromCents(
    sections
      .flatMap(section => section.rows)
      .reduce((sum, row) => sum + toCents(row.amountVat ?? 0), 0),
  );
  const partOf = (kind: IOwnerCardRow["kind"]) =>
    sumCents(categoryRows.filter(r => r.kind === kind).map(r => r.amount));
  // The fixed shares together; a correction of a lease end and a reading's
  // part of the excess each on a line of their own (M29).
  const baseParts = ownerBaseParts({
    rows: categoryRows.filter(r => r.kind === "base"),
    isSplitByReadings: key => {
      const keyDist = distribution?.byCat[key];
      return (
        !isUnitBreakdownKey(key) &&
        !keyDist?.key &&
        (keyDist?.method === "consumption" || keyDist?.method === "company")
      );
    },
    ownerPctOf: key => pctByCat[key],
  });
  const infoRows: IInfoRow[] = [
    ...(baseParts.fixed != null
      ? [
          {
            label: getLocalizedText("settlement.summary.card.tag_fixed_share"),
            value: fmt(baseParts.fixed),
          },
        ]
      : []),
    ...baseParts.corrections.map(part => ({
      label: departureCorrectionLabel(part.key) ?? part.key,
      value: fmt(part.amount),
    })),
    ...baseParts.excess.map(part => ({
      label: getLocalizedText("settlement.summary.card.excess_share_of", {
        category: categoryLabel(part.key),
      }),
      value: fmt(part.amount),
    })),
    ...(categoryRows.some(r => r.kind === "vacancy")
      ? [
          {
            label: getLocalizedText("settlement.summary.card.tag_vacancy"),
            value: fmt(partOf("vacancy")),
          },
        ]
      : []),
    ...categoryRows
      .filter(r => r.kind === "departure_difference")
      .map(r => ({ label: kindTag(r), value: fmt(r.amount) })),
    ...restInfoRows(sections),
  ];

  return (
    <CollapsibleCard
      title={title}
      totals={
        <>
          {/* The owner's total incl. VAT is the card's last line: no
              "Totaal kosten incl. btw" above it repeating it. */}
          <CardTotals
            totals={[
              ...costVatTotals({
                excl: fromCents(toCents(total) - toCents(totVat)),
                vat: totVat,
                vatRows: vatRateRowsOf(sections),
              }),
              {
                id: "total",
                // A negative total is the owner's credit (M29).
                label: getLocalizedText(
                  total < -0.005
                    ? "settlement.summary.card.owner_credit"
                    : "settlement.distribution.owner_bears",
                ),
                info: {
                  rows: infoRows,
                  note:
                    sections.length > 0
                      ? getLocalizedText(
                          "settlement.summary.card.invoices_note",
                        )
                      : undefined,
                },
                amount: Math.abs(total),
                isFinal: true,
              },
            ]}
          />
          {onOpenStatement && <StatementButton onClick={onOpenStatement} />}
        </>
      }
    >
      <CardTable
        variant={variant}
        sections={sections}
        amountHeading={getLocalizedText(
          "settlement.summary.card.column_charged",
        )}
        isOwner
        onOpenCost={
          onOpenCost &&
          (invoiceId => onOpenCost(invoiceId, { ownerUnitIds: unitIds }))
        }
      />
    </CollapsibleCard>
  );
};

// The id the wizard gives its own corrections among the confirmed ones, for
// AlreadySettledCard (M28): the settlement has no id before it is saved.
const OWN_CORRECTIONS_ID = "own";

// The note on a confirmed tenant card whose lease end moved since (Y11).
const LEASE_END_CARD_KEYS: Record<TDepartureLeaseEnd["kind"], string> = {
  later: "settlement.summary.card.lease_end_later",
  open: "settlement.summary.card.lease_end_open",
  earlier: "settlement.summary.card.lease_end_earlier",
};

const LEASE_END_KEYS: Record<
  "departure" | "already_settled",
  Record<TDepartureLeaseEnd["kind"], string>
> = {
  departure: {
    later: "settlement.departure.lease_end_later",
    open: "settlement.departure.lease_end_open",
    earlier: "settlement.departure.lease_end_earlier",
  },
  already_settled: {
    later: "settlement.already_settled.lease_end_later",
    open: "settlement.already_settled.lease_end_open",
    earlier: "settlement.already_settled.lease_end_earlier",
  },
};

// The note on a departure whose lease no longer ends on the day it settled
// up to (see departureLeaseEnd): on the departure settlement itself, or under
// "Al afgerekend bij vertrek" in a later settlement.
export const departureLeaseEndText = (params: {
  change: TDepartureLeaseEnd;
  tenant: string;
  until: string;
  on: "departure" | "already_settled";
}): string => {
  const { change, tenant, until, on } = params;
  return getLocalizedText(LEASE_END_KEYS[on][change.kind], {
    tenant,
    until: fmtDate(until),
    ...(change.kind === "open" ? {} : { end: fmtDate(change.end) }),
  });
};

// On a confirmed settlement that listed a departure whose lease end moved
// since (Y10, decision 3): who carried the days in between here, with the
// amount, and that the next settlement of the unit or the building corrects
// it. Measured from the lease end this settlement charged the lease up to
// (line.leaseEnd), so a settlement confirmed after the end moved says
// nothing it did not do (M01). The plain text when nothing can be counted.
const confirmedLeaseEndText = (params: {
  change: TDepartureLeaseEnd;
  line: IAlreadySettledLine;
  lines: IAlreadySettledLine[];
  lease?: Pick<ContractRef, "startDate" | "endDate">;
  costs: Invoice[];
}): string => {
  const { change, line, lines, lease, costs } = params;
  const chargedUpTo = line.leaseEnd ?? line.until;
  const cents = lease
    ? departedDaysCents({
        held: Object.fromEntries(
          lines
            .filter(other => other.settlementId === line.settlementId)
            .map(other => [other.invoiceId, other.amount]),
        ),
        lease,
        until: line.until,
        between: [chargedUpTo, lease.endDate],
        invoices: costs,
      })
    : 0;
  if (cents === 0) {
    return departureLeaseEndText({
      change,
      tenant: line.tenantName,
      until: chargedUpTo,
      on: "already_settled",
    });
  }
  return getLocalizedText(
    change.kind === "earlier"
      ? "settlement.already_settled.lease_end_earlier_confirmed"
      : "settlement.already_settled.lease_end_later_confirmed",
    {
      tenant: line.tenantName,
      until: fmtDate(chargedUpTo),
      end: change.kind === "open" ? "—" : fmtDate(change.end),
      amount: fmt(Math.abs(cents) / 100),
    },
  );
};

// A departure whose moved lease end a settlement corrected already (M28):
// where, and the net correction incl. VAT; on the settlement that made it,
// its own correction line. Undefined while nothing corrected it.
const leaseEndCorrectedText = (params: {
  line: IAlreadySettledLine;
  lease?: Pick<ContractRef, "endDate">;
  corrections?: ICorrectionsBySettlement[];
  ownId?: string;
}): string | undefined => {
  const { line, lease, corrections, ownId } = params;
  if (!lease || !corrections) return undefined;
  const corrected = leaseEndCorrectedIn({
    departureSettlementId: line.settlementId,
    leaseEnd: lease.endDate,
    bySettlement: corrections,
    ownId,
  });
  if (!corrected) return undefined;
  return getLocalizedText(
    corrected.isOwn
      ? "settlement.already_settled.lease_end_corrected_here"
      : "settlement.already_settled.lease_end_corrected",
    {
      tenant: line.tenantName,
      until: fmtDate(line.leaseEnd ?? line.until),
      settlements: corrected.settlementNames.join(", "),
      amount: fmt(
        fromCents(toCents(corrected.amount) + toCents(corrected.vat)),
      ),
    },
  );
};

// The cost shares departure settlements already settled (see
// ISettledDeparture): shown to the broker and the owner, never charged again
// (Lewis, 2026-10-08). One row per departed lease and cost, with a link to
// the departure settlement on its unit's costs tab, and a note per departure
// whose lease no longer ends on the day it settled up to.
export const AlreadySettledCard: React.FC<{
  lines: IAlreadySettledLine[];
  unitLabelOf: (unitId: string) => string;
  // The leases as they are now.
  contracts: Pick<ContractRef, "id" | "endDate" | "startDate">[];
  // A confirmed settlement: its costs. It settled the days of a moved lease
  // end as they were then, so its note says who carried them, and what the
  // next settlement corrects (Y10, decision 3).
  confirmedCosts?: Invoice[];
  // The corrections the settlements of the tree made, this one's own
  // included (`ownId`): once one corrected a departure's moved lease end,
  // its note says where, instead of "de volgende afrekening" (M28).
  corrections?: ICorrectionsBySettlement[];
  ownId?: string;
}> = ({
  lines,
  unitLabelOf,
  contracts,
  confirmedCosts,
  corrections,
  ownId,
}) => {
  if (lines.length === 0) return null;
  // Excl. VAT, its VAT under it, as the party cards (Lewis, 2026-10-19).
  const total = fromCents(
    lines.reduce((sum, line) => sum + toCents(line.amount), 0),
  );
  const totalVat = fromCents(
    lines.reduce((sum, line) => sum + toCents(line.vat), 0),
  );
  const departureLines = [
    ...new Map(lines.map(line => [line.settlementId, line])).values(),
  ];
  const leaseEndNotes = departureLines.flatMap(line => {
    // A confirmed settlement: against the end it charged the lease up to.
    const change = departureLeaseEnd({
      until: (confirmedCosts && line.leaseEnd) || line.until,
      lease: contracts.find(c => c.id === line.contractId),
    });
    return change ? [{ id: line.settlementId, line, change }] : [];
  });
  return (
    <CollapsibleCard
      title={getLocalizedText("settlement.already_settled.title")}
      subtitle={
        <TextStyle variation="subdued" size="small">
          {getLocalizedText("settlement.already_settled.intro")}
        </TextStyle>
      }
      totals={
        <SummaryBlock>
          <SummaryLine emphasized>
            <span className="label">
              {getLocalizedText("settlement.already_settled.total")}
            </span>
            <span className="value">
              <MoneyWithVat amount={total} vat={totalVat} />
            </span>
          </SummaryLine>
        </SummaryBlock>
      }
    >
      <DataTable
        columnContentTypes={["text", "text", "text", "numeric"]}
        headings={[
          getLocalizedText("costs.field.tenant"),
          getLocalizedText("costs.field.property"),
          getLocalizedText("costs.field.cost"),
          getLocalizedText("settlement.already_settled.amount"),
        ]}
        rows={lines.map(line => ({
          id: `${line.settlementId}-${line.invoiceId}`,
          content: [
            <div>
              <TextStyle variation="strong">{line.tenantName}</TextStyle>
              <div>
                <Button
                  appearance="link"
                  url={`/properties/${line.unitId}/costs?settlementId=${line.settlementId}`}
                >
                  {getLocalizedText("settlement.already_settled.until", {
                    until: fmtDate(line.until),
                  })}
                </Button>
              </div>
            </div>,
            unitLabelOf(line.unitId),
            categoryLabel(line.label),
            <MoneyWithVat amount={line.amount} vat={line.vat} />,
          ],
        }))}
      />
      {leaseEndNotes.map(({ id, line, change }) => (
        <div key={id} style={{ marginTop: 8 }}>
          <TextStyle
            variation={change.kind === "earlier" ? "warn" : "subdued"}
            size="small"
            element="div"
          >
            {leaseEndCorrectedText({
              line,
              lease: contracts.find(c => c.id === line.contractId),
              corrections,
              ownId,
            }) ??
              (confirmedCosts
                ? confirmedLeaseEndText({
                    change,
                    line,
                    lines,
                    lease: contracts.find(c => c.id === line.contractId),
                    costs: confirmedCosts,
                  })
                : departureLeaseEndText({
                    change,
                    tenant: line.tenantName,
                    until: line.until,
                    on: "already_settled",
                  }))}
          </TextStyle>
        </div>
      ))}
    </CollapsibleCard>
  );
};

export const Step4Summary: React.FC<
  StepContext & { result: ComputeResult }
> = ({
  result,
  draft,
  settlement,
  invoices,
  assignable,
  contracts,
  keys,
  units,
  ownerNames,
  departureContractId,
  settledInvoiceIds,
  departureSettled,
  confirmedCorrections,
}) => {
  // The method a departure settlement split each category by (see
  // resolveDistributionMethod), the same as the engine.
  const isDeparture = !!departureContractId;
  // A unit's full address with its box spelled out ("Scenariolaan 1 bus 3,
  // 9000 Gent"), as the costs and provisions steps name it.
  const propertyOf = (id?: string) =>
    assignable.find(a => String(a.id) === String(id));
  const unitAddressOf = (unitId: string): string => {
    const p = propertyOf(unitId);
    if (p) return unitAddressLabel(p);
    return (units ?? []).find(u => u.id === unitId)?.code ?? unitId;
  };
  // The tenant's rental-contract period, shown under the card title; the
  // "Prorata" in each amount's "i" then prorates the cost's period against
  // it.
  const contractDatesFor = (
    pc: PerContractResult,
  ): { from: string; to: string } | undefined => {
    const c = contracts.find(x => x.id === pc.contractId);
    return c ? { from: c.startDate, to: c.endDate } : undefined;
  };

  // How each common (group) category of the chosen costs was split: its
  // key (the tenant's share and its hover), or the method's label for a
  // reading or a metering company's amount ("Werkelijk verbruik").
  const distByCat = React.useMemo(() => {
    const out: Record<string, CategoryDistribution> = {};
    const equalKey = keys.find(k => k.isSystem && k.isEqual) ?? keys[0];
    for (const inv of invoices) {
      if (
        !draft.selectedInvoiceIds[inv.id] ||
        !settlementEligible(inv) ||
        inv.scope !== "common"
      ) {
        continue;
      }
      const cat = inv.category;
      const method = resolveDistributionMethod(cat, draft.distributionMethod, {
        isDeparture,
      });
      if (method === "key") {
        const chosenId = draft.selectedKeyId[cat];
        const chosen = keys.find(k => k.id === chosenId);
        // A key that was chosen and has since been deleted is not shown as
        // the equal one: `result.missingKeyCategories` warns about it above.
        out[cat] =
          chosenId && !chosen ? { label: "—" } : { key: chosen ?? equalKey };
      } else {
        // The owners' part follows the category's key, or the equal split
        // when none was chosen (see ownerPartKeyName).
        const ownerPartKeyId = draft.selectedKeyId[cat];
        out[cat] = {
          label: methodLabel(method, cat, { isDeparture }),
          method,
          perOccupant: isPerOccupant(
            (method === "consumption"
              ? draft.consumption[cat]
              : draft.companyAmounts[cat]) ?? {},
          ),
          ownerPartKey: ownerPartKeyId
            ? keys.find(k => k.id === ownerPartKeyId)
            : keys.find(k => k.isSystem && k.isEqual),
        };
      }
    }
    return out;
  }, [
    invoices,
    draft.selectedInvoiceIds,
    draft.distributionMethod,
    draft.selectedKeyId,
    draft.consumption,
    draft.companyAmounts,
    keys,
    isDeparture,
  ]);
  const distribution: ICardDistribution = {
    byCat: distByCat,
    units: units ?? [],
  };

  // The key that split the owners' part of each consumption or
  // metering-company category, for the owner cards (see ownerPartKeyName).
  const ownerPartKeyByCat = React.useMemo(() => {
    const out: Record<string, string> = {};
    const equalKeyName = keys.find(k => k.isSystem && k.isEqual)?.name;
    for (const cat of Object.keys(distByCat) as Category[]) {
      const name = ownerPartKeyName({
        method: resolveDistributionMethod(cat, draft.distributionMethod, {
          isDeparture,
        }),
        keyId: draft.selectedKeyId[cat],
        keyNameOf: keyId => keys.find(k => k.id === keyId)?.name,
        equalKeyName,
      });
      if (name) out[cat] = name;
    }
    return out;
  }, [
    distByCat,
    draft.distributionMethod,
    draft.selectedKeyId,
    keys,
    isDeparture,
  ]);

  // Effective VAT rate + period per category, from the selected costs — lets us
  // show a btw column on each tenant's (prorated) category share.
  const catMeta = React.useMemo(
    () =>
      buildCatMeta(
        invoices.filter(
          inv => draft.selectedInvoiceIds[inv.id] && settlementEligible(inv),
        ),
      ),
    [invoices, draft.selectedInvoiceIds],
  );

  // The whole settlement's figures, for the readings and who carries the
  // rest of each invoice.
  const shareContext = draftCostShareContext({
    result,
    split: draft,
    contracts,
    catMeta,
    distribution,
    ownerNames,
    isDeparture,
    assignable,
    units,
  });

  // A summary row opens its cost in the drawer the cost list opens it in
  // (Lewis, 2026-10-19), with how this draft splits it and the row's party
  // marked. The drawer opens through the URL (DetailDrawers, outside the
  // wizard), so the wizard stays as it is under it: only its query changes,
  // which the leave guard lets through (it compares paths) and nothing in
  // the wizard reads; its step, its draft and their autosave stay.
  const [, setDrawerParams] = useQueryParams({ propertyCostId: StringParam });
  const settlementName = getDraftSettlementName(draft).trim() || undefined;
  const openCost = (invoiceId: string, party?: ICostSplitParty) => {
    setCostDrawerSplit({
      invoiceId,
      splitContext: shareContext,
      settlementName,
      openedFor: party,
    });
    setDrawerParams({ propertyCostId: invoiceId });
  };
  // Leaving the summary takes the draft's figures back from the drawer.
  React.useEffect(() => () => setCostDrawerSplit(undefined), []);

  const tenantLines = result.perContract.filter(
    pc => Math.abs(pc.cost) > 0.001 || Math.abs(pc.provision) > 0.001,
  );

  // Keep a tenant card per contract ("{adres} - {huurder}"), but on a group
  // settlement order them by pand so multiple (e.g. sequential) tenants of the
  // same unit sit together, oldest first.
  const orderedTenantLines = React.useMemo(() => {
    if (settlement.scope !== "group") return tenantLines;
    const addrOf = (unitId: string) =>
      (units ?? []).find(u => u.id === unitId)?.address ?? unitId;
    return [...tenantLines].sort((a, b) => {
      const byUnit = addrOf(a.unitId).localeCompare(addrOf(b.unitId), "nl", {
        numeric: true,
        sensitivity: "base",
      });
      if (byUnit !== 0) return byUnit;
      const fa = contractDatesFor(a)?.from ?? "";
      const fb = contractDatesFor(b)?.from ?? "";
      return fa.localeCompare(fb);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settlement.scope, tenantLines, units]);

  const unitProp = propertyOf(settlement.unitId);
  const unitAddress = unitProp
    ? unitAddressLabel(unitProp)
    : settlement.unitId ?? getLocalizedText("system.property");

  // Full unit address (incl. bus number) for the tenant of a given contract.
  // On group scope each tenant sits on a different unit, so we resolve per
  // contract; fall back to the frozen unitCode for older snapshots.
  const addressForContract = (pc: PerContractResult): string => {
    if (settlement.scope === "unit") return unitAddress;
    const prop = propertyOf(pc.unitId);
    return prop ? unitAddressLabel(prop) : pc.unitCode;
  };

  // An owner's card: "{unit address} - {owner}" for an owner of one unit,
  // "{building address} - {owner}" with "Eigenaar van 4 panden" under it
  // for an owner of several, their addresses in its "i" (Lewis,
  // 2026-10-19). With what their units weigh of the building by the key
  // that split the owners' part ("55/85 van het gebouw").
  const ownerPartKeys = ownerPartKeysOf(distByCat);
  const ownerTitleOf = (unitIds: string[], name: string) => {
    if (settlement.scope === "unit") {
      return <PartyTitle name={name} address={unitAddress} />;
    }
    const note = ownerUnitsNote({
      units: unitIds.map(unitId => ({
        unitId,
        address: unitAddressOf(unitId),
      })),
      shares: ownerKeyShares({
        keys: ownerPartKeys,
        building: { units: distribution.units },
        ownerUnitIds: unitIds,
        period: draft.period,
      }),
    });
    return (
      <PartyTitle
        name={name}
        address={
          unitIds.length === 1
            ? unitAddressOf(unitIds[0])
            : buildingAddressOf(propertyOf(settlement.groupId))
        }
        note={note}
      />
    );
  };

  const titleFor = (pc: PerContractResult) => (
    <PartyTitle
      name={pc.tenantName}
      address={addressForContract(pc)}
      note={parentNote(
        contracts.find(c => c.id === pc.contractId)?.parentNames,
      )}
    />
  );

  // No cost chosen, and the broker confirmed the period had none: every
  // provision comes back, said at the top (Lewis, 2026-10-17).
  const isWithoutChosenCosts =
    chosenCosts({
      invoices,
      selectedInvoiceIds: draft.selectedInvoiceIds,
      excludedInvoiceIds: settledInvoiceIds,
    }).length === 0;

  return (
    <div>
      <StepHeader>{getLocalizedText("settlement.summary.title")}</StepHeader>
      {isWithoutChosenCosts && !isNothingToSettle(result) && (
        <div style={{ marginBottom: 16 }}>
          <Banner variation="info" hasDismiss={false}>
            {getNoCostsText({
              on: "summary",
              // A departure names its one tenant (M44).
              departureTenant: contracts.find(c => c.id === departureContractId)
                ?.tenantName,
            })}
          </Banner>
        </div>
      )}
      <div style={{ marginBottom: 24 }}>
        <TextStyle variation="subdued">
          {getLocalizedText("settlement.summary.intro")}
        </TextStyle>
      </div>
      {/* What every final statement says about costs still to come
          (decision 4, 2026-10-20); the PDF prints the same sentence. */}
      {isDeparture && (
        <div style={{ marginBottom: 16 }}>
          <Banner variation="info" hasDismiss={false}>
            {getLocalizedText("settlement.departure.later_costs_note")}
          </Banner>
        </div>
      )}

      <ContractOverlapWarning
        contracts={contracts}
        invoices={invoices}
        draft={draft}
        assignable={assignable}
      />

      {result.missingKeyCategories.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Banner variation="warning" hasDismiss={false}>
            {getLocalizedText("settlement.summary.missing_key", {
              categories: result.missingKeyCategories
                .map(categoryLabel)
                .join(", "),
            })}
          </Banner>
        </div>
      )}

      {/* A lease settled up to its last day already (Y06): a final
          settlement of € 0 that says so. */}
      {departureSettled?.isFull && isNothingToSettle(result) && (
        <div style={{ marginBottom: 16 }}>
          <DepartureSettledNote settled={departureSettled} />
        </div>
      )}
      {isNothingToSettle(result) && !departureSettled?.isFull ? (
        <TextStyle variation="subdued">
          {getLocalizedText("settlement.summary.nothing_to_settle")}
        </TextStyle>
      ) : (
        <Stack vertical spacing="loose">
          {orderedTenantLines.map(pc => (
            <SummaryCard
              key={pc.contractId}
              variant="wizard"
              title={titleFor(pc)}
              period={draft.period}
              pc={pc}
              catMeta={catMeta}
              onOpenCost={openCost}
              contract={contractDatesFor(pc)}
              lease={contracts.find(c => c.id === pc.contractId)}
              distribution={distribution}
              shareContext={shareContext}
            />
          ))}
          {/* Only when it has lines: the Stack gives every child its slot
              and spacing, also one that renders nothing (a double gap
              before the owners, Lewis 2026-10-19). */}
          {result.alreadySettled.length > 0 && (
            <AlreadySettledCard
              lines={result.alreadySettled}
              contracts={contracts}
              unitLabelOf={unitAddressOf}
              // This settlement's own corrections, and those confirmed
              // before (M28).
              corrections={[
                ...(confirmedCorrections ?? []),
                {
                  settlementId: OWN_CORRECTIONS_ID,
                  name: getDraftSettlementName(draft),
                  corrections: result.departureCorrections ?? [],
                },
              ]}
              ownId={OWN_CORRECTIONS_ID}
            />
          )}
          {/* One card per owner of the units: each owner only carries the
              part of their own panden. */}
          {result.owners
            // An owner whose departure difference cancels the rest still
            // sees the row that explains it.
            .filter(ownerHasPart)
            .map(owner => (
              <OwnerSummaryCard
                key={owner.ownerKey || "owner"}
                variant="wizard"
                title={ownerTitleOf(
                  owner.unitIds,
                  ownerNames[owner.ownerKey] ||
                    getLocalizedText("costs.split.owner"),
                )}
                baseByCat={owner.baseByCat}
                vacancyByCat={owner.vacancyByCat}
                pctByCat={result.landlordPctByCat}
                vatByCat={owner.vatByCat}
                catMeta={catMeta}
                onOpenCost={openCost}
                ownerPartKeyByCat={ownerPartKeyByCat}
                departureDiffsByCat={departureDiffsByCat(owner.lines)}
                distribution={distribution}
                unitIds={owner.unitIds}
                lines={owner.lines}
                shareContext={shareContext}
                departureCorrections={result.departureCorrections}
              />
            ))}
        </Stack>
      )}
    </div>
  );
};
